import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as Tabs from "@radix-ui/react-tabs";
import {
    AlertTriangle,
    BarChart3,
    CalendarDays,
    Check,
    CheckCircle2,
    Download,
    History,
    Info,
    Loader2,
    Pencil,
    Plus,
    RefreshCw,
    Table2,
    Trash2,
    Trophy,
    Users,
    X,
} from "lucide-react";
import { toast } from "sonner";
import Breadcrumb from "../components/Breadcrumb";
import LeagueContextActions from "../components/LeagueContextActions";
import Header from "../components/Header";
import ProviderBetaBanner from "../components/ProviderBetaBanner";
import Tooltip from "../components/Tooltip";
import { useAuth } from "../components/AuthContext";
import { fetchWithRetry, safeFetch } from "../utils/api";
import { providerPersistsHistory } from "../utils/providerPolicy";
import { getCookie } from "../utils/csrf";
import usePageMeta from "../utils/usePageMeta";

const HISTORY_TABS = [
    { value: "coverage", label: "Coverage", icon: Table2 },
    { value: "records", label: "All-Time Records", icon: Trophy },
    { value: "leaderboards", label: "Owner Leaderboards", icon: BarChart3 },
    { value: "rivalries", label: "Rivalries", icon: Users },
    { value: "aliases", label: "Owner Aliases", icon: Pencil },
    { value: "seasons", label: "Season Browser", icon: CalendarDays },
    { value: "refresh", label: "Refresh Status", icon: RefreshCw },
];
const HISTORY_TAB_VALUES = new Set(HISTORY_TABS.map((tab) => tab.value));
const HISTORY_DATA_KEYS = ["coverage", "records", "leaderboards", "rivalries"];

const LEADERBOARD_OPTIONS = [
    { key: "all_time_wins", label: "Wins", format: "integer" },
    { key: "all_time_losses", label: "Losses", format: "integer" },
    { key: "win_percentage", label: "Win %", format: "percent" },
    { key: "points_for", label: "Points For", format: "number" },
    { key: "points_per_game", label: "Points per Game", format: "number" },
    { key: "points_against", label: "Points Against", format: "number" },
    { key: "weekly_high_scores", label: "Weekly Highs", format: "integer" },
    { key: "top_weekly_finishes", label: "Top Finishes", format: "integer" },
    { key: "playoff_appearances", label: "Playoff Apps", format: "integer" },
    { key: "playoff_wins", label: "Playoff Wins", format: "integer" },
];

const recordGroupLabels = {
    highest_single_game_score: "Scores",
    lowest_single_game_score: "Scores",
    best_average_points_season: "Season Averages",
    worst_average_points_season: "Season Averages",
    longest_win_streak: "Streaks",
    longest_loss_streak: "Streaks",
    best_lineup_efficiency: "Lineup Efficiency",
    worst_lineup_efficiency: "Lineup Efficiency",
    biggest_projection_beat: "Projection",
    biggest_projection_miss: "Projection",
};

const BEST_TRIO_TOOLTIP =
    "Best Trio: The combined fantasy points from a team's highest-scoring QB, highest-scoring RB, and highest-scoring WR or TE that week.";

const formatNumber = (value, format = "number") => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "-";
    }
    const number = Number(value);
    if (format === "percent") {
        return `${(number * 100).toFixed(1)}%`;
    }
    if (format === "integer") {
        return String(Math.round(number));
    }
    return number.toLocaleString(undefined, {
        maximumFractionDigits: 2,
    });
};

const formatDecimal = (value, digits = 2) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "-";
    }
    return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
};

const formatInteger = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "-";
    }
    return Math.round(Number(value)).toLocaleString();
};

const formatRecordValue = (record) => {
    if (!record) return "-";
    const value = formatNumber(record.value, record.value_format);
    return record.value_units ? `${value} ${record.value_units}` : value;
};

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

const statusClass = (tone) => {
    if (tone === "good") return "bg-green-50 text-green-700 border-green-200";
    if (tone === "warn") return "bg-mustard/15 text-forest border-mustard/40";
    if (tone === "danger") return "bg-coral-light text-coral border-coral/40";
    return "bg-surface text-text-muted border-border";
};

const compactWeekList = (weeks) => {
    const sorted = Array.from(
        new Set((weeks || []).map((week) => Number(week)).filter(Number.isFinite))
    ).sort((a, b) => a - b);
    if (!sorted.length) return "-";

    const ranges = [];
    let start = sorted[0];
    let previous = sorted[0];
    for (const week of sorted.slice(1)) {
        if (week === previous + 1) {
            previous = week;
            continue;
        }
        ranges.push(start === previous ? `${start}` : `${start}-${previous}`);
        start = week;
        previous = week;
    }
    ranges.push(start === previous ? `${start}` : `${start}-${previous}`);
    return ranges.join(", ");
};

const seasonStatus = (season) => {
    if (season?.data_refresh?.is_season_complete) {
        return { label: "Complete", tone: "good" };
    }
    if (season?.has_weekly_rows || season?.data_refresh?.last_fetched_at) {
        return { label: "Incomplete", tone: "warn" };
    }
    return { label: "Not Fetched", tone: "neutral" };
};

const isWorstRecord = (record) =>
    /lowest|worst|least|loss|miss/.test(record?.key || "");

const parseApiResponse = async (response, fallbackMessage) => {
    try {
        return await response.json();
    } catch (err) {
        const statusText = response?.status ? ` (HTTP ${response.status})` : "";
        const message = `${fallbackMessage}. The server returned a non-JSON response${statusText}.`;
        throw Object.assign(new Error(message), {
            payload: {
                status: "error",
                error: fallbackMessage,
                detail:
                    "The server returned HTML or another non-JSON response. For refresh requests, this usually means the request timed out or the server returned an error page.",
            },
        });
    }
};

const filenameFromContentDisposition = (header, fallback) => {
    if (!header) return fallback;
    const utfMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) {
        return decodeURIComponent(utfMatch[1].replace(/["']/g, ""));
    }
    const basicMatch = header.match(/filename="?([^";]+)"?/i);
    return basicMatch?.[1] || fallback;
};

const downloadBlob = (blob, filename) => {
    if (typeof window === "undefined" || !window.URL?.createObjectURL) {
        throw new Error("Browser download support unavailable.");
    }
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL?.(objectUrl);
};

const pairRecordSort = (a, b) => {
    const winDiff = Number(b.wins || 0) - Number(a.wins || 0);
    if (winDiff !== 0) return winDiff;
    const lossDiff = Number(a.losses || 0) - Number(b.losses || 0);
    if (lossDiff !== 0) return lossDiff;
    const tieDiff = Number(b.ties || 0) - Number(a.ties || 0);
    if (tieDiff !== 0) return tieDiff;
    return String(a.owner || "").localeCompare(String(b.owner || ""));
};

const formatPairRecordTitle = (pair) => {
    const records = pair.owner_records || [];
    if (records.length >= 2) {
        const sorted = [...records].sort(pairRecordSort);
        const leader = sorted[0];
        const opponent = sorted[1];
        const leaderWins = Number(leader.wins || 0);
        const opponentWins = Number(opponent.wins || 0);
        const ties = Math.max(
            Number(leader.ties || 0),
            Number(opponent.ties || 0)
        );
        const tieText =
            ties > 0 ? ` (${ties} ${ties === 1 ? "tie" : "ties"})` : "";
        if (leaderWins === opponentWins) {
            return `${leader.owner} and ${opponent.owner} are tied ${leaderWins} - ${opponentWins}${tieText}`;
        }
        return `${leader.owner} leads ${opponent.owner} ${leaderWins} - ${opponentWins}${tieText}`;
    }
    return (pair.owners || []).join(" vs. ");
};

const formatHighestScoreDetail = (record) => {
    if (!record) return "";
    const parts = [];
    if (record.owner) parts.push(`by ${record.owner}`);
    if (record.year) {
        parts.push(
            `${record.year}${record.week ? ` Week ${record.week}` : ""}${
                record.is_playoff ? " playoff" : ""
            }`
        );
    }
    return parts.join(", ");
};

const rankLeaderboardRows = (rows) => {
    let previousValue = null;
    let previousRank = null;
    return (rows || []).map((row, index) => {
        const value = row?.value ?? 0;
        const rank =
            previousRank !== null && value === previousValue
                ? previousRank
                : index + 1;
        previousValue = value;
        previousRank = rank;
        return {
            ...row,
            rank,
        };
    });
};

const StatusPill = ({ children, tone = "neutral" }) => (
    <span
        className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-bold uppercase tracking-wide ${statusClass(
            tone
        )}`}
    >
        {children}
    </span>
);

const SummaryCard = ({ label, value, detail }) => (
    <div className="rounded border border-border bg-white px-4 py-3">
        <p className="text-xs font-bold uppercase tracking-wide text-text-muted">
            {label}
        </p>
        <p className="mt-1 text-2xl font-black text-forest">{value}</p>
        {detail && <p className="mt-1 text-xs font-semibold text-text-muted">{detail}</p>}
    </div>
);

const EmptyState = ({ children }) => (
    <div className="rounded border border-border bg-white px-4 py-8 text-center text-sm font-semibold text-text-muted">
        {children}
    </div>
);

const SkeletonBlock = ({ className = "" }) => (
    <div className={`animate-pulse rounded bg-border/70 ${className}`} />
);

const ErrorState = ({ message }) => (
    <div className="rounded border border-coral/40 bg-coral-light px-4 py-3 text-sm font-semibold text-coral">
        {message}
    </div>
);

const CoverageSkeleton = () => (
    <section data-testid="coverage-skeleton" className="mt-6 space-y-6">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            {Array.from({ length: 6 }).map((_, index) => (
                <div
                    key={`coverage-summary-${index}`}
                    className="rounded border border-border bg-white px-4 py-3"
                >
                    <SkeletonBlock className="h-3 w-20" />
                    <SkeletonBlock className="mt-3 h-8 w-16" />
                    <SkeletonBlock className="mt-2 h-3 w-24" />
                </div>
            ))}
        </div>

        <div className="rounded border border-border bg-white px-4 py-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div className="space-y-2">
                    <SkeletonBlock className="h-6 w-40" />
                    <SkeletonBlock className="h-4 w-28" />
                </div>
                <div className="grid gap-3 sm:grid-cols-[1fr,1fr,auto]">
                    <SkeletonBlock className="h-10 w-full sm:w-36" />
                    <SkeletonBlock className="h-10 w-full sm:w-36" />
                    <SkeletonBlock className="h-10 w-full sm:w-32" />
                </div>
            </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
            <div className="rounded border border-border bg-white p-4">
                <div className="mb-4 flex items-center justify-between gap-3">
                    <SkeletonBlock className="h-7 w-40" />
                    <SkeletonBlock className="h-9 w-44" />
                </div>
                <div className="space-y-3">
                    {Array.from({ length: 4 }).map((_, index) => (
                        <SkeletonBlock
                            key={`coverage-row-${index}`}
                            className="h-12 w-full"
                        />
                    ))}
                </div>
            </div>
            <div className="rounded border border-border bg-white p-4">
                <SkeletonBlock className="mb-4 h-7 w-24" />
                <div className="space-y-3">
                    {Array.from({ length: 3 }).map((_, index) => (
                        <SkeletonBlock
                            key={`member-row-${index}`}
                            className="h-12 w-full"
                        />
                    ))}
                </div>
            </div>
        </div>
    </section>
);

const RecordsSkeleton = () => (
    <section className="mt-6 space-y-4">
        <div className="border-b-2 border-ds-text pb-2">
            <SkeletonBlock className="h-7 w-48" />
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
            {Array.from({ length: 2 }).map((_, columnIndex) => (
                <div key={`records-column-${columnIndex}`} className="space-y-3">
                    <SkeletonBlock className="h-6 w-32" />
                    {Array.from({ length: 3 }).map((__, cardIndex) => (
                        <div
                            key={`records-card-${columnIndex}-${cardIndex}`}
                            className="rounded border border-border bg-white px-4 py-3"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="space-y-2">
                                    <SkeletonBlock className="h-3 w-20" />
                                    <SkeletonBlock className="h-6 w-40" />
                                </div>
                                <SkeletonBlock className="h-8 w-16" />
                            </div>
                            <div className="mt-3 border-t border-border pt-3 space-y-2">
                                <SkeletonBlock className="h-4 w-28" />
                                <SkeletonBlock className="h-3 w-24" />
                            </div>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    </section>
);

const LeaderboardsSkeleton = () => (
    <section className="mt-6 space-y-4">
        <div className="flex flex-col gap-3 border-b-2 border-ds-text pb-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-2">
                <SkeletonBlock className="h-7 w-52" />
                <SkeletonBlock className="h-4 w-36" />
            </div>
            <SkeletonBlock className="h-10 w-full lg:w-56" />
        </div>
        <div className="rounded border border-border bg-white p-4 space-y-3">
            {Array.from({ length: 6 }).map((_, index) => (
                <SkeletonBlock
                    key={`leaderboard-row-${index}`}
                    className="h-11 w-full"
                />
            ))}
        </div>
    </section>
);

const RivalriesSkeleton = () => (
    <section className="mt-6 space-y-6">
        <div className="border-b-2 border-ds-text pb-2">
            <SkeletonBlock className="h-7 w-32" />
        </div>
        <div className="grid gap-6 lg:grid-cols-[0.9fr,1.6fr]">
            <div className="rounded border border-border bg-white px-4 py-4 space-y-4">
                <SkeletonBlock className="h-5 w-32" />
                <SkeletonBlock className="h-10 w-full" />
                <SkeletonBlock className="h-48 w-full" />
                <SkeletonBlock className="h-10 w-full" />
            </div>
            <div className="space-y-4">
                {Array.from({ length: 2 }).map((_, index) => (
                    <div
                        key={`rivalry-card-${index}`}
                        className="rounded border border-border bg-white px-4 py-4 space-y-4"
                    >
                        <div className="space-y-2">
                            <SkeletonBlock className="h-3 w-20" />
                            <SkeletonBlock className="h-7 w-44" />
                            <SkeletonBlock className="h-4 w-52" />
                        </div>
                        <div className="grid gap-3 sm:grid-cols-3">
                            {Array.from({ length: 3 }).map((__, metricIndex) => (
                                <SkeletonBlock
                                    key={`rivalry-metric-${index}-${metricIndex}`}
                                    className="h-20 w-full"
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const CoverageDependentSkeleton = ({ title, cards = 3 }) => (
    <section className="mt-6 space-y-4">
        <div className="border-b-2 border-ds-text pb-2">
            <SkeletonBlock className="h-7 w-40" />
        </div>
        <div className="grid gap-3 md:grid-cols-2">
            {Array.from({ length: cards }).map((_, index) => (
                <div
                    key={`${title}-card-${index}`}
                    className="rounded border border-border bg-white px-4 py-4 space-y-3"
                >
                    <SkeletonBlock className="h-6 w-20" />
                    <SkeletonBlock className="h-4 w-28" />
                    <SkeletonBlock className="h-20 w-full" />
                    <SkeletonBlock className="h-9 w-28" />
                </div>
            ))}
        </div>
    </section>
);

const OwnerScopeToggle = ({
    includeAllOwners,
    setIncludeAllOwners,
    latestSeasonYear,
    currentOwnerCount,
    loading,
}) => (
    <div className="flex flex-col items-start gap-1">
        <button
            type="button"
            onClick={() => setIncludeAllOwners((current) => !current)}
            role="switch"
            aria-checked={includeAllOwners}
            className="inline-flex items-center gap-2 text-sm font-bold text-text"
        >
            <span
                className={`relative inline-flex h-6 w-11 items-center rounded-full border transition-colors ${
                    includeAllOwners
                        ? "border-forest bg-forest"
                        : "border-border bg-surface"
                }`}
            >
                <span
                    className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                        includeAllOwners ? "translate-x-5" : "translate-x-1"
                    }`}
                />
            </span>
            <span className={`transition-colors ${
                includeAllOwners
                    ? "text-forest"
                    : "text-text"
            }`}>
                {includeAllOwners
                    ? "All historical owners"
                    : "Current owners only"}
            </span>
        </button>
        <p className="text-xs font-semibold text-text-muted">
            {includeAllOwners
                ? "Showing all historical owners."
                : latestSeasonYear
                  ? `Showing ${currentOwnerCount || 0} current owner${
                        currentOwnerCount === 1 ? "" : "s"
                    } from ${latestSeasonYear}.`
                  : "Showing current owners only."}
            {loading ? " Updating..." : ""}
        </p>
    </div>
);


const LeagueHistoryPage = () => {
    const { provider, leagueYear, leagueId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();

    const tabParam = searchParams.get("tab");
    const activeTab = HISTORY_TAB_VALUES.has(tabParam) ? tabParam : "coverage";
    const setActiveTab = useCallback(
        (value) => {
            const next = new URLSearchParams(searchParams);
            if (value === "coverage") {
                next.delete("tab");
            } else {
                next.set("tab", value);
            }
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams]
    );
    const [leagueData, setLeagueData] = useState(null);
    const [coverage, setCoverage] = useState(null);
    const [records, setRecords] = useState(null);
    const [leaderboards, setLeaderboards] = useState(null);
    const [rivalries, setRivalries] = useState(null);
    const [tabLoading, setTabLoading] = useState({
        coverage: true,
        records: true,
        leaderboards: true,
        rivalries: true,
    });
    const [errors, setErrors] = useState({});
    const [ownerRows, setOwnerRows] = useState(null);
    const [teamOwnerRows, setTeamOwnerRows] = useState(null);
    const [ownerAliasMeta, setOwnerAliasMeta] = useState(null);
    const [editingOwnerName, setEditingOwnerName] = useState(null);
    const [editingCanonical, setEditingCanonical] = useState("");
    const [savingOwnerName, setSavingOwnerName] = useState(null);
    const [selectedLeaderboard, setSelectedLeaderboard] =
        useState("all_time_wins");
    const [includeAllOwners, setIncludeAllOwners] = useState(false);
    const [creatingRivalry, setCreatingRivalry] = useState(false);
    const [updatingRivalryId, setUpdatingRivalryId] = useState(null);
    const [deletingRivalryId, setDeletingRivalryId] = useState(null);
    const [refreshTarget, setRefreshTarget] = useState({
        year: leagueYear,
        week: "",
    });
    const [refreshing, setRefreshing] = useState(false);
    const [refreshResult, setRefreshResult] = useState(null);
    const [refreshError, setRefreshError] = useState(null);
    const [refreshActivity, setRefreshActivity] = useState(null);
    const [exportRange, setExportRange] = useState({
        startYear: "",
        endYear: "",
    });
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState(null);
    const [importingAllSeasons, setImportingAllSeasons] = useState(false);
    const historyRequestIdRef = useRef(0);
    const tabScrollRef = useRef(null);

    const leagueName =
        leagueData?.league_name ||
        coverage?.league_name ||
        records?.league_name ||
        leaderboards?.league_name ||
        rivalries?.league_name;
    const leagueUrl = `/fantasy_stats/league/${provider}/${leagueYear}/${leagueId}/`;
    const canRefresh = Boolean(
        coverage?.can_refresh ?? (leagueData?.is_owner || user?.is_ktc_admin)
    );

    usePageMeta({
        title: leagueName
            ? `${leagueName} History`
            : `League History - ${leagueYear}`,
        description: leagueName
            ? `Historical coverage, all-time records, owner leaderboards, and refresh status for ${leagueName}.`
            : undefined,
        canonical: `/fantasy_stats/league-history/${provider}/${leagueYear}/${leagueId}/`,
    });

    useEffect(() => {
        const node = tabScrollRef.current;
        if (!node || typeof window === "undefined") return undefined;

        const resetScroll = () => {
            node.scrollLeft = 0;
        };
        if (typeof window.requestAnimationFrame !== "function") {
            resetScroll();
            return undefined;
        }

        const frame = window.requestAnimationFrame(() => {
            resetScroll();
        });

        return () => window.cancelAnimationFrame(frame);
    }, [leagueId, leagueYear, provider]);

    const historyUrls = useMemo(
        () => ({
            coverage: `/api/historical/${provider}/${leagueYear}/${leagueId}/coverage/`,
            export: `/api/historical/${provider}/${leagueYear}/${leagueId}/export/`,
            records: `/api/historical/${provider}/${leagueYear}/${leagueId}/records/`,
            leaderboards: `/api/historical/${provider}/${leagueYear}/${leagueId}/leaderboards/`,
            rivalries: `/api/historical/${provider}/${leagueYear}/${leagueId}/rivalries/`,
        }),
        [provider, leagueYear, leagueId]
    );

    const loadOwnerAliases = useCallback(async (leaguePk) => {
        const res = await safeFetch(
            `/api/league/${leaguePk}/owner-aliases/`,
            {},
            true,
            1
        );
        if (res?.owner_rows != null) {
            setOwnerRows(res.owner_rows);
        } else if (res?.aliases != null) {
            setOwnerRows(
                res.aliases.map((alias) => ({
                    owner_key: alias.owner_key || "",
                    raw_name: alias.alias,
                    alias_id: alias.id,
                    canonical_name: alias.canonical_name,
                    has_alias: true,
                    seasons: [],
                    team_names: [],
                    conflicts: [],
                }))
            );
        } else {
            setOwnerRows([]);
        }
        setTeamOwnerRows(res?.team_rows || []);
        setOwnerAliasMeta({
            seasonYears: res?.season_years || [],
            conflictCount: res?.conflict_count || 0,
        });
    }, []);

    const setHistoryState = useCallback((key, value) => {
        if (key === "coverage") {
            setCoverage(value);
            return;
        }
        if (key === "records") {
            setRecords(value);
            return;
        }
        if (key === "leaderboards") {
            setLeaderboards(value);
            return;
        }
        if (key === "rivalries") {
            setRivalries(value);
        }
    }, []);

    const clearHistoryState = useCallback((keys) => {
        keys.forEach((key) => {
            setHistoryState(key, null);
        });
    }, [setHistoryState]);

    const loadHistoryData = useCallback(
        async ({
            keys = HISTORY_DATA_KEYS,
            preserveData = false,
        } = {}) => {
            const requestId = ++historyRequestIdRef.current;
            const keyList = Array.isArray(keys) ? keys : [keys];
            const endpoints = {
                coverage: historyUrls.coverage,
                records: `${historyUrls.records}?scope=all-time&owner_scope=all`,
                leaderboards: `${historyUrls.leaderboards}?limit=50&top_n=3&owner_scope=all`,
                rivalries: historyUrls.rivalries,
            };
            const fallbackErrors = {
                coverage: "Coverage unavailable.",
                records: "Records unavailable.",
                leaderboards: "Leaderboards unavailable.",
                rivalries: "Rivalries unavailable.",
            };

            if (!preserveData) {
                clearHistoryState(keyList);
            }

            setErrors((current) => {
                const next = { ...current };
                keyList.forEach((key) => {
                    delete next[key];
                });
                return next;
            });
            setTabLoading((current) => ({
                ...current,
                ...Object.fromEntries(keyList.map((key) => [key, true])),
            }));

            await Promise.all(
                keyList.map(async (key) => {
                    try {
                        const payload = await safeFetch(endpoints[key], {}, false, 1);
                        if (historyRequestIdRef.current !== requestId) return;
                        setHistoryState(key, payload);
                    } catch (err) {
                        if (historyRequestIdRef.current !== requestId) return;
                        setErrors((current) => ({
                            ...current,
                            [key]: err?.message || fallbackErrors[key],
                        }));
                    } finally {
                        if (historyRequestIdRef.current !== requestId) return;
                        setTabLoading((current) => ({
                            ...current,
                            [key]: false,
                        }));
                    }
                })
            );
        },
        [clearHistoryState, historyUrls, setHistoryState]
    );

    useEffect(() => {
        safeFetch(`/api/league/${provider}/${leagueYear}/${leagueId}/`, {}, false, 1)
            .then((data) => {
                if (data?.redirect) {
                    navigate(data.redirect);
                } else {
                    setLeagueData(data);
                }
            })
            .catch(() => null);
    }, [provider, leagueYear, leagueId, navigate]);

    useEffect(() => {
        if (!leagueData?.id || !leagueData?.is_owner) {
            setOwnerRows(null);
            setTeamOwnerRows(null);
            setOwnerAliasMeta(null);
            setEditingOwnerName(null);
            setEditingCanonical("");
            setSavingOwnerName(null);
            return;
        }

        loadOwnerAliases(leagueData.id).catch(() => {
            setOwnerRows([]);
            setTeamOwnerRows([]);
            setOwnerAliasMeta({ seasonYears: [], conflictCount: 0 });
        });
    }, [leagueData?.id, leagueData?.is_owner, loadOwnerAliases]);

    useEffect(() => {
        loadHistoryData();
        return () => {
            historyRequestIdRef.current += 1;
        };
    }, [loadHistoryData]);

    useEffect(() => {
        setRefreshTarget({ year: leagueYear, week: "" });
    }, [leagueYear, leagueId]);

    const coverageLoading = tabLoading.coverage;

    const handleImportOldLeagues = async () => {
        if (!leagueData?.is_owner) return;

        setImportingAllSeasons(true);
        try {
            const csrftoken = getCookie("csrftoken");
            const response = await fetchWithRetry(
                `/api/league/${leagueId}/import-all-seasons/`,
                {
                    method: "POST",
                    headers: { "X-CSRFToken": csrftoken },
                    credentials: "include",
                },
                1
            );
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(payload.error || "Import failed. Please try again.");
            }

            const parts = [];
            if (payload.imported?.length) {
                parts.push(
                    `${payload.imported.length} season${
                        payload.imported.length === 1 ? "" : "s"
                    } imported`
                );
            }
            if (payload.already_existed?.length) {
                parts.push(
                    `${payload.already_existed.length} already existed`
                );
            }
            if (payload.skipped?.length) {
                parts.push(`${payload.skipped.length} not available`);
            }
            toast.success(parts.length ? parts.join(" · ") : "Nothing new to import");

            await loadHistoryData({ preserveData: true });
            if (leagueData?.id && leagueData?.is_owner) {
                loadOwnerAliases(leagueData.id).catch(() => null);
            }
        } catch (err) {
            toast.error(err.message || "Import failed. Please try again.");
        } finally {
            setImportingAllSeasons(false);
        }
    };

    const linkedSeasonTargets = useMemo(() => {
        const seasons = coverage?.linked_seasons || [];
        if (seasons.length) {
            return seasons
                .map((season) => ({
                    provider: season.provider || provider,
                    year: String(season.year),
                    leagueId: season.league_id || leagueId,
                    leagueName: season.league_name || coverage?.league_name,
                    canRefresh: Boolean(
                        season.can_refresh ??
                            coverage?.can_refresh ??
                            (leagueData?.is_owner || user?.is_ktc_admin)
                    ),
                }))
                .sort((a, b) => Number(b.year) - Number(a.year));
        }
        return [
            {
                provider,
                year: String(leagueYear),
                leagueId,
                leagueName,
                canRefresh: canRefresh,
            },
        ];
    }, [
        coverage,
        provider,
        leagueYear,
        leagueId,
        leagueName,
        leagueData?.is_owner,
        user?.is_ktc_admin,
        canRefresh,
    ]);

    const refreshableSeasonTargets = useMemo(
        () => linkedSeasonTargets.filter((season) => season.canRefresh),
        [linkedSeasonTargets]
    );

    const refreshSelectionTargets = canRefresh
        ? refreshableSeasonTargets
        : linkedSeasonTargets;

    const selectedRefreshSeason =
        refreshSelectionTargets.find(
            (season) => String(season.year) === String(refreshTarget.year)
        ) || refreshSelectionTargets[0];

    useEffect(() => {
        if (!canRefresh || refreshableSeasonTargets.length === 0) return;
        const selectedIsRefreshable = refreshableSeasonTargets.some(
            (season) => String(season.year) === String(refreshTarget.year)
        );
        if (!selectedIsRefreshable) {
            setRefreshTarget((prev) => ({
                ...prev,
                year: refreshableSeasonTargets[0].year,
            }));
        }
    }, [canRefresh, refreshableSeasonTargets, refreshTarget.year]);

    const runRefresh = async (mode, targetOverride = {}) => {
        const target = { ...refreshTarget, ...targetOverride };
        const explicitTargetSeason =
            target.provider && target.leagueId && target.year
                ? {
                      provider: target.provider,
                      year: String(target.year),
                      leagueId: String(target.leagueId),
                  }
                : null;

        const targetSeason =
            explicitTargetSeason ||
            refreshableSeasonTargets.find(
                (season) => String(season.year) === String(target.year)
            ) ||
            selectedRefreshSeason;

        if (!canRefresh || !targetSeason) return;

        const week = Number(target.week);
        if (mode === "week" && (!Number.isInteger(week) || week < 1)) {
            setRefreshError("Enter a valid week number before refreshing a week.");
            return;
        }

        const base = `/api/historical/${targetSeason.provider}/${targetSeason.year}/${targetSeason.leagueId}`;
        const endpoint =
            mode === "week" ? `${base}/refresh-week/${week}/` : `${base}/refresh/`;

        setRefreshing(true);
        setRefreshError(null);
        setRefreshResult(null);
        setRefreshActivity({
            status: "running",
            mode,
            year: targetSeason.year,
            week: mode === "week" ? week : null,
            startedAt: new Date().toISOString(),
        });

        try {
            const response = await fetchWithRetry(endpoint, { method: "POST" }, 0);
            const payload = await parseApiResponse(response, "Refresh failed");
            if (!response.ok) {
                throw Object.assign(
                    new Error(payload.error || payload.detail || "Refresh failed."),
                    { payload }
                );
            }
            setRefreshResult(payload);
            setRefreshActivity((activity) => ({
                ...(activity || {}),
                status: "succeeded",
                mode,
                year: payload.year || targetSeason.year,
                week: payload.week || (mode === "week" ? week : null),
                rowsUpserted: payload.rows_upserted || 0,
                finishedAt: new Date().toISOString(),
                resultStatus: payload.status,
            }));
            toast.success(
                mode === "week"
                    ? `Week ${payload.week} refresh complete.`
                    : `${payload.year} refresh complete.`
            );
            await loadHistoryData({ silent: true });
        } catch (err) {
            const payload = err.payload;
            const inProgress = payload?.status === "refresh_in_progress";
            setRefreshResult(payload || null);
            setRefreshError(inProgress ? null : err.message || "Refresh failed.");
            setRefreshActivity((activity) => ({
                ...(activity || {}),
                status: inProgress ? "running" : "failed",
                mode,
                year: payload?.year || targetSeason.year,
                week: payload?.week || (mode === "week" ? week : null),
                rowsUpserted: payload?.rows_upserted || 0,
                finishedAt: inProgress ? null : new Date().toISOString(),
                resultStatus: payload?.status,
                error: payload?.error || err.message || "Refresh failed.",
            }));
            toast.error(err.message || "Refresh failed.");
        } finally {
            setRefreshing(false);
        }
    };

    const beginAliasEdit = (row) => {
        setEditingOwnerName(
            row.row_type === "team"
                ? `team:${row.pair_key}`
                : row.owner_key || `legacy:${row.raw_name}`
        );
        setEditingCanonical(row.canonical_name || row.raw_name);
    };

    const cancelAliasEdit = () => {
        setEditingOwnerName(null);
        setEditingCanonical("");
    };

    const handleSaveAlias = async (row) => {
        const canonicalName = editingCanonical.trim();
        if (!leagueData?.id || !row?.raw_name || !canonicalName) return;
        if (canonicalName === row.canonical_name) {
            cancelAliasEdit();
            return;
        }

        const rowIdentity =
            row.row_type === "team"
                ? `team:${row.pair_key}`
                : row.owner_key || `legacy:${row.raw_name}`;
        setSavingOwnerName(rowIdentity);
        try {
            const isExistingAlias = Boolean(row.alias_id);
            const response = await fetchWithRetry(
                isExistingAlias
                    ? `/api/league/${leagueData.id}/owner-aliases/${row.alias_id}/`
                    : `/api/league/${leagueData.id}/owner-aliases/`,
                {
                    method: isExistingAlias ? "PATCH" : "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(
                        isExistingAlias
                            ? { canonical_name: canonicalName }
                            : {
                                  owner_key: row.owner_key || "",
                                  alias: row.raw_name,
                                  canonical_name: canonicalName,
                              }
                    ),
                },
                0
            );
            const data = await response.json();
            if (response.ok) {
                await loadOwnerAliases(leagueData.id);
                cancelAliasEdit();
                toast.success(isExistingAlias ? "Alias updated." : "Alias added.");
            } else {
                toast.error(data.error || "Failed to save alias.");
            }
        } catch {
            toast.error("An unexpected error occurred.");
        } finally {
            setSavingOwnerName(null);
        }
    };

    const handleDeleteAlias = async (row) => {
        if (!leagueData?.id || !row?.alias_id) return;
        const rowIdentity =
            row.row_type === "team"
                ? `team:${row.pair_key}`
                : row.owner_key || `legacy:${row.raw_name}`;
        setSavingOwnerName(rowIdentity);
        try {
            const response = await fetchWithRetry(
                `/api/league/${leagueData.id}/owner-aliases/${row.alias_id}/`,
                { method: "DELETE" },
                0
            );
            if (response.ok) {
                await loadOwnerAliases(leagueData.id);
                if (editingOwnerName === rowIdentity) cancelAliasEdit();
                toast.success("Alias removed.");
            } else {
                toast.error("Failed to remove alias.");
            }
        } catch {
            toast.error("An unexpected error occurred.");
        } finally {
            setSavingOwnerName(null);
        }
    };

    const runSeasonBackfills = async (seasons) => {
        const targets = seasons.filter((season) => season.can_refresh);
        for (const season of targets) {
            const linkedSeason =
                (coverage?.linked_seasons || []).find(
                    (linked) => String(linked.year) === String(season.year)
                ) || null;
            if (!linkedSeason) continue;
            await runRefresh("season", {
                year: String(season.year),
                week: "",
                provider: linkedSeason?.provider,
                leagueId: linkedSeason?.league_id,
            });
        }
    };

    const runExport = async () => {
        const startYear = Number(exportRange.startYear);
        const endYear = Number(exportRange.endYear);
        if (
            !Number.isInteger(startYear) ||
            !Number.isInteger(endYear) ||
            startYear > endYear
        ) {
            setExportError("Select a valid season range.");
            return;
        }

        const params = new URLSearchParams({
            format: "csv",
            start_year: String(startYear),
            end_year: String(endYear),
        });
        const endpoint = `${historyUrls.export}?${params.toString()}`;

        setExporting(true);
        setExportError(null);
        try {
            const response = await fetchWithRetry(endpoint, { method: "GET" }, 0);
            if (!response.ok) {
                let payload = {};
                try {
                    payload = await response.json();
                } catch (err) {
                    payload = {};
                }
                throw new Error(payload.error || "Historical export failed.");
            }

            const blob = await response.blob();
            const filename = filenameFromContentDisposition(
                response.headers?.get?.("Content-Disposition"),
                `doritostats-historical-${provider}-${leagueId}-${startYear}-${endYear}.csv`
            );
            downloadBlob(blob, filename);
            toast.success("Historical CSV downloaded.");
        } catch (err) {
            setExportError(err.message || "Historical export failed.");
            toast.error(err.message || "Historical export failed.");
        } finally {
            setExporting(false);
        }
    };

    const sortRivalryItems = useCallback(
        (items) =>
            [...items].sort((a, b) => {
                const nameCompare = String(a.name || "").localeCompare(
                    String(b.name || ""),
                    undefined,
                    { sensitivity: "base" }
                );
                if (nameCompare !== 0) return nameCompare;
                return Number(a.id || 0) - Number(b.id || 0);
            }),
        []
    );

    const upsertRivalry = useCallback(
        (payload) => {
            if (!payload?.id) return;
            setRivalries((current) => {
                if (!current) return current;
                const rivalriesList = current.rivalries || [];
                const existingIndex = rivalriesList.findIndex(
                    (rivalry) => String(rivalry.id) === String(payload.id)
                );
                const nextRivalries =
                    existingIndex >= 0
                        ? rivalriesList.map((rivalry, index) =>
                              index === existingIndex ? payload : rivalry
                          )
                        : [...rivalriesList, payload];
                return {
                    ...current,
                    rivalry_count: nextRivalries.length,
                    rivalries: sortRivalryItems(nextRivalries),
                };
            });
        },
        [sortRivalryItems]
    );

    const removeRivalry = useCallback((rivalryId) => {
        setRivalries((current) => {
            if (!current) return current;
            const nextRivalries = (current.rivalries || []).filter(
                (rivalry) => String(rivalry.id) !== String(rivalryId)
            );
            return {
                ...current,
                rivalry_count: nextRivalries.length,
                rivalries: nextRivalries,
            };
        });
    }, []);

    const createRivalry = async ({ name, ownerMembers }) => {
        if (ownerMembers.length < 2) {
            toast.error("Select at least two owners.");
            return false;
        }
        setCreatingRivalry(true);
        try {
            const response = await fetchWithRetry(
                historyUrls.rivalries,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ name, owner_names: ownerMembers }),
                },
                0
            );
            const payload = await response.json();
            if (!response.ok) {
                throw new Error(
                    payload.error || payload.detail || "Failed to create rivalry."
                );
            }
            upsertRivalry(payload);
            toast.success("Rivalry saved.");
            return true;
        } catch (err) {
            toast.error(err.message || "Failed to create rivalry.");
            return false;
        } finally {
            setCreatingRivalry(false);
        }
    };

    const deleteRivalry = async (rivalryId) => {
        if (
            typeof window !== "undefined" &&
            window.confirm &&
            !window.confirm("Delete this rivalry?")
        ) {
            return;
        }
        setDeletingRivalryId(rivalryId);
        try {
            const response = await fetchWithRetry(
                `${historyUrls.rivalries}${rivalryId}/`,
                { method: "DELETE" },
                0
            );
            const payload = await response.json();
            if (!response.ok) {
                throw new Error(
                    payload.error || payload.detail || "Failed to delete rivalry."
                );
            }
            removeRivalry(rivalryId);
            toast.success("Rivalry deleted.");
        } catch (err) {
            toast.error(err.message || "Failed to delete rivalry.");
        } finally {
            setDeletingRivalryId(null);
        }
    };

    const updateRivalryName = async (rivalryId, name) => {
        setUpdatingRivalryId(rivalryId);
        try {
            const response = await fetchWithRetry(
                `${historyUrls.rivalries}${rivalryId}/`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ name }),
                },
                0
            );
            const payload = await response.json();
            if (!response.ok) {
                throw new Error(
                    payload.error || payload.detail || "Failed to update rivalry."
                );
            }
            upsertRivalry(payload);
            toast.success("Rivalry name updated.");
            return true;
        } catch (err) {
            toast.error(err.message || "Failed to update rivalry.");
            return false;
        } finally {
            setUpdatingRivalryId(null);
        }
    };

    const coverageSummary = useMemo(() => {
        const seasons = coverage?.season_coverage || [];
        const rows = seasons.reduce((total, season) => total + season.row_count, 0);
        const completedWeeks = seasons.reduce(
            (total, season) => total + season.completed_week_count,
            0
        );
        const completeRefreshes = seasons.filter(
            (season) => season.data_refresh?.is_season_complete
        ).length;
        const incompleteSeasons = seasons.filter(
            (season) => !season.data_refresh?.is_season_complete
        ).length;
        const lastFetched = seasons
            .map((season) => season.data_refresh?.last_fetched_at)
            .filter(Boolean)
            .sort()
            .at(-1);
        return {
            linkedSeasons: coverage?.linked_years?.length || 0,
            rows,
            completedWeeks,
            completeRefreshes,
            incompleteSeasons,
            lastFetched,
        };
    }, [coverage]);

    const exportYearOptions = useMemo(() => {
        const years = new Set([
            ...(coverage?.linked_years || []),
            ...(coverage?.season_coverage || []).map((season) => season.year),
        ]);
        return Array.from(years)
            .map((year) => String(year))
            .sort((a, b) => Number(a) - Number(b));
    }, [coverage]);

    useEffect(() => {
        if (exportYearOptions.length === 0) return;
        setExportRange((current) => {
            const firstYear = exportYearOptions[0];
            const lastYear = exportYearOptions[exportYearOptions.length - 1];
            const startYear = exportYearOptions.includes(current.startYear)
                ? current.startYear
                : firstYear;
            const endYear = exportYearOptions.includes(current.endYear)
                ? current.endYear
                : lastYear;
            const normalizedStart =
                Number(startYear) <= Number(endYear) ? startYear : endYear;
            const normalizedEnd =
                Number(startYear) <= Number(endYear) ? endYear : startYear;

            if (
                current.startYear === normalizedStart &&
                current.endYear === normalizedEnd
            ) {
                return current;
            }
            return {
                startYear: normalizedStart,
                endYear: normalizedEnd,
            };
        });
    }, [exportYearOptions]);

    if (!providerPersistsHistory(provider)) {
        return (
            <div className="min-h-screen min-w-0 bg-bg">
                <Header />
                <div className="bg-forest border-b-4 border-mustard">
                    <div className="mx-auto max-w-6xl min-w-0 px-6 py-6">
                        <Breadcrumb
                            items={[
                                { label: "Dashboard", to: "/dashboard" },
                                { label: leagueName || `League ${leagueId}`, to: leagueUrl },
                                { label: "History" },
                            ]}
                        />
                        <h1 className="font-display text-4xl font-black leading-none text-white">
                            League History
                        </h1>
                    </div>
                </div>
                <main className="mx-auto w-full max-w-3xl px-6 py-12" data-testid="history-unavailable">
                    <div className="rounded-xl border border-border bg-white p-8 space-y-4">
                        <h2 className="font-display text-2xl font-bold text-ds-text">
                            History isn't available for Yahoo leagues
                        </h2>
                        <p className="text-sm leading-relaxed text-text">
                            Yahoo's API terms don't allow Doritostats to keep a permanent copy of weekly
                            Yahoo stats, which is what powers the cross-season History, Records, and
                            Leaderboard pages. Everything for the current season (awards, standings,
                            luck index, power rankings, box scores, simulations, and the League Cup) is
                            still available from the league page, where it is loaded fresh from Yahoo.
                        </p>
                        <Link
                            to={leagueUrl}
                            className="inline-flex items-center rounded bg-forest px-4 py-2 text-sm font-bold text-white hover:bg-forest/90"
                        >
                            Back to league page
                        </Link>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen min-w-0 bg-bg">
            <Header />
            <div className="bg-forest border-b-4 border-mustard">
                <div className="mx-auto max-w-6xl min-w-0 px-6 py-6">
                    <Breadcrumb
                        items={[
                            { label: "Dashboard", to: "/dashboard" },
                            {
                                label: leagueName || `League ${leagueId}`,
                                to: leagueUrl,
                            },
                            { label: "History" },
                        ]}
                    />
                    <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                        <div className="min-w-0">
                            <p className="text-mustard text-xs font-sans font-bold uppercase tracking-widest mb-2">
                                Historical League Group
                            </p>
                            <h1 className="break-words font-display text-4xl font-black leading-none text-white">
                                {leagueName || (
                                    <span className="inline-block h-9 w-64 animate-pulse rounded bg-white/20" />
                                )}
                            </h1>
                            <p className="mt-2 text-sm font-semibold text-white/50">
                                {coverage
                                    ? `${coverage.provider.toUpperCase()} ${coverage.canonical_id}`
                                    : `${provider.toUpperCase()} ${leagueId}`}
                            </p>
                        </div>
                        <LeagueContextActions league={leagueData} provider={provider} leagueYear={leagueYear} leagueId={leagueId} activePage="history" />
                    </div>
                </div>
            </div>

            <main className="mx-auto w-full max-w-6xl min-w-0 space-y-6 overflow-x-hidden px-4 py-8 sm:px-6">
                <ProviderBetaBanner
                    provider={provider}
                    leagueId={leagueId}
                    leagueYear={leagueYear}
                    leagueName={leagueName}
                />

                <Tabs.Root value={activeTab} onValueChange={setActiveTab} className="block w-full min-w-0 max-w-full overflow-hidden">
                    <div className="w-full min-w-0 max-w-full overflow-hidden rounded border border-border bg-white">
                        <div
                            ref={tabScrollRef}
                            className="w-full min-w-0 max-w-full overflow-visible sm:touch-pan-x sm:overflow-x-auto sm:overscroll-x-contain"
                            aria-label="History sections"
                        >
                            <Tabs.List className="flex flex-wrap gap-1 p-1 sm:min-w-max sm:flex-nowrap">
                                {HISTORY_TABS.map(({ value, label, icon: Icon }) => (
                                    <Tabs.Trigger
                                        key={value}
                                        value={value}
                                        className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-bold uppercase tracking-wide text-text-muted transition-colors hover:text-forest data-[state=active]:bg-forest data-[state=active]:text-white"
                                    >
                                        <Icon className="h-3.5 w-3.5" />
                                        {label}
                                    </Tabs.Trigger>
                                ))}
                            </Tabs.List>
                        </div>
                    </div>

                    <Tabs.Content value="coverage" className="outline-none">
                        <CoverageTab
                            coverage={coverage}
                            summary={coverageSummary}
                            loading={coverageLoading}
                            canRefresh={canRefresh}
                            refreshing={refreshing}
                            exportYearOptions={exportYearOptions}
                            exportRange={exportRange}
                            setExportRange={setExportRange}
                            exporting={exporting}
                            exportError={exportError}
                            onRunExport={runExport}
                            canImportOldSeasons={Boolean(leagueData?.is_owner)}
                            importingOldSeasons={importingAllSeasons}
                            onImportOldSeasons={handleImportOldLeagues}
                            onRunRefresh={runRefresh}
                            onRunSeasonBackfills={runSeasonBackfills}
                            error={errors.coverage}
                        />
                    </Tabs.Content>

                    <Tabs.Content value="records" className="outline-none">
                        <RecordsTab
                            records={records}
                            loading={tabLoading.records}
                            error={errors.records}
                            includeAllOwners={includeAllOwners}
                            setIncludeAllOwners={setIncludeAllOwners}
                        />
                    </Tabs.Content>

                    <Tabs.Content value="leaderboards" className="outline-none">
                        <LeaderboardsTab
                            leaderboards={leaderboards}
                            loading={tabLoading.leaderboards}
                            selectedLeaderboard={selectedLeaderboard}
                            setSelectedLeaderboard={setSelectedLeaderboard}
                            error={errors.leaderboards}
                            includeAllOwners={includeAllOwners}
                            setIncludeAllOwners={setIncludeAllOwners}
                        />
                    </Tabs.Content>

                    <Tabs.Content value="rivalries" className="outline-none">
                        <RivalriesTab
                            rivalries={rivalries}
                            loading={tabLoading.rivalries}
                            creating={creatingRivalry}
                            updatingId={updatingRivalryId}
                            deletingId={deletingRivalryId}
                            onCreate={createRivalry}
                            onRename={updateRivalryName}
                            onDelete={deleteRivalry}
                            error={errors.rivalries}
                        />
                    </Tabs.Content>

                    <Tabs.Content value="aliases" className="outline-none">
                        <OwnerAliasesTab
                            isOwner={Boolean(leagueData?.is_owner)}
                            ownerRows={ownerRows}
                            teamOwnerRows={teamOwnerRows}
                            ownerAliasMeta={ownerAliasMeta}
                            editingOwnerName={editingOwnerName}
                            editingCanonical={editingCanonical}
                            setEditingCanonical={setEditingCanonical}
                            savingOwnerName={savingOwnerName}
                            onBeginAliasEdit={beginAliasEdit}
                            onCancelAliasEdit={cancelAliasEdit}
                            onSaveAlias={handleSaveAlias}
                            onDeleteAlias={handleDeleteAlias}
                        />
                    </Tabs.Content>

                    <Tabs.Content value="seasons" className="outline-none">
                        <SeasonBrowserTab
                            coverage={coverage}
                            loading={coverageLoading}
                            canRefresh={canRefresh}
                            onRefreshSeason={(year) => {
                                setRefreshTarget({ year: String(year), week: "" });
                                setActiveTab("refresh");
                            }}
                            error={errors.coverage}
                        />
                    </Tabs.Content>

                    <Tabs.Content value="refresh" className="outline-none">
                        <RefreshTab
                            coverage={coverage}
                            loading={coverageLoading}
                            canRefresh={canRefresh}
                            refreshTarget={refreshTarget}
                            setRefreshTarget={setRefreshTarget}
                            linkedSeasonTargets={linkedSeasonTargets}
                            refreshableSeasonTargets={refreshableSeasonTargets}
                            refreshing={refreshing}
                            refreshResult={refreshResult}
                            refreshError={refreshError}
                            refreshActivity={refreshActivity}
                            onRunRefresh={runRefresh}
                            error={errors.coverage}
                        />
                    </Tabs.Content>
                </Tabs.Root>
            </main>
        </div>
    );
};

const CoverageTab = ({
    coverage,
    summary,
    loading,
    canRefresh,
    refreshing,
    exportYearOptions,
    exportRange,
    setExportRange,
    exporting,
    exportError,
    onRunExport,
    canImportOldSeasons,
    importingOldSeasons,
    onImportOldSeasons,
    onRunRefresh,
    onRunSeasonBackfills,
    error,
}) => {
    if (error) return <ErrorState message={error} />;
    if (!coverage) {
        return loading ? (
            <CoverageSkeleton />
        ) : (
            <EmptyState>No coverage data loaded yet.</EmptyState>
        );
    }

    const incompleteRefreshableSeasons = (coverage.season_coverage || []).filter(
        (season) =>
            season.can_refresh && !season.data_refresh?.is_season_complete
    );

    const linkedSeasonByYear = (coverage.linked_seasons || []).reduce(
        (acc, season) => {
            acc[String(season.year)] = season;
            return acc;
        },
        {}
    );

    return (
        <section className="mt-6 max-w-full space-y-6 overflow-hidden">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
                <SummaryCard
                    label="Linked Seasons"
                    value={summary.linkedSeasons}
                    detail={(coverage.linked_years || []).join(", ") || "-"}
                />
                <SummaryCard
                    label="Stored Rows"
                    value={formatInteger(summary.rows)}
                />
                <SummaryCard
                    label="Stored Weeks"
                    value={formatInteger(summary.completedWeeks)}
                />
                <SummaryCard
                    label="Complete Refreshes"
                    value={summary.completeRefreshes}
                />
                <SummaryCard
                    label="Incomplete"
                    value={summary.incompleteSeasons}
                    detail="Need backfill or pending season"
                />
                <SummaryCard
                    label="Last Fetched"
                    value={summary.lastFetched ? "Updated" : "-"}
                    detail={formatDate(summary.lastFetched)}
                />
            </div>

            <HistoricalExportPanel
                years={exportYearOptions}
                range={exportRange}
                setRange={setExportRange}
                exporting={exporting}
                error={exportError}
                rowCount={summary.rows}
                onRunExport={onRunExport}
            />

            <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
                <SeasonCoverageTable
                    seasons={coverage.season_coverage || []}
                    linkedSeasonByYear={linkedSeasonByYear}
                    canRefresh={canRefresh}
                    refreshing={refreshing}
                    canImportOldSeasons={canImportOldSeasons}
                    importingOldSeasons={importingOldSeasons}
                    onImportOldSeasons={onImportOldSeasons}
                    onRunRefresh={onRunRefresh}
                    onRunSeasonBackfills={() =>
                        onRunSeasonBackfills(incompleteRefreshableSeasons)
                    }
                    hasRefreshableIncomplete={
                        incompleteRefreshableSeasons.length > 0
                    }
                />
                <MembersPanel members={coverage.linked_members || []} />
            </div>
        </section>
    );
};

const OwnerAliasesTab = ({
    isOwner,
    ownerRows,
    teamOwnerRows,
    ownerAliasMeta,
    editingOwnerName,
    editingCanonical,
    setEditingCanonical,
    savingOwnerName,
    onBeginAliasEdit,
    onCancelAliasEdit,
    onSaveAlias,
    onDeleteAlias,
}) => {
    if (!isOwner) {
        return (
            <section className="mt-6">
                <EmptyState>
                    Only the league owner can manage owner name aliases.
                </EmptyState>
            </section>
        );
    }

    return (
        <section className="mt-6">
            <OwnerAliasesPanel
                ownerRows={ownerRows}
                teamOwnerRows={teamOwnerRows}
                ownerAliasMeta={ownerAliasMeta}
                editingOwnerName={editingOwnerName}
                editingCanonical={editingCanonical}
                setEditingCanonical={setEditingCanonical}
                savingOwnerName={savingOwnerName}
                onBeginAliasEdit={onBeginAliasEdit}
                onCancelAliasEdit={onCancelAliasEdit}
                onSaveAlias={onSaveAlias}
                onDeleteAlias={onDeleteAlias}
            />
        </section>
    );
};

const HistoricalExportPanel = ({
    years,
    range,
    setRange,
    exporting,
    error,
    rowCount,
    onRunExport,
}) => {
    const updateRange = (key, value) => {
        setRange((current) => ({ ...current, [key]: value }));
    };
    const disabled =
        exporting ||
        years.length === 0 ||
        rowCount === 0 ||
        !range.startYear ||
        !range.endYear;

    return (
        <div className="rounded border border-border bg-white px-4 py-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <Download className="h-5 w-5 text-forest" />
                        <h2 className="font-display text-2xl font-bold text-ds-text">
                            Historical Export
                        </h2>
                    </div>
                    <p className="mt-1 text-sm font-semibold text-text-muted">
                        {formatInteger(rowCount)} weekly matchup rows
                    </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-[1fr,1fr,auto]">
                    <label className="text-sm font-bold text-text">
                        Start Season
                        <select
                            value={range.startYear}
                            onChange={(event) =>
                                updateRange("startYear", event.target.value)
                            }
                            disabled={years.length === 0 || exporting}
                            className="mt-1 w-full rounded border border-border bg-white px-3 py-2 text-sm font-semibold"
                        >
                            {years.map((year) => (
                                <option key={`export-start-${year}`} value={year}>
                                    {year}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="text-sm font-bold text-text">
                        End Season
                        <select
                            value={range.endYear}
                            onChange={(event) =>
                                updateRange("endYear", event.target.value)
                            }
                            disabled={years.length === 0 || exporting}
                            className="mt-1 w-full rounded border border-border bg-white px-3 py-2 text-sm font-semibold"
                        >
                            {years.map((year) => (
                                <option key={`export-end-${year}`} value={year}>
                                    {year}
                                </option>
                            ))}
                        </select>
                    </label>
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={onRunExport}
                        className="inline-flex items-center justify-center gap-2 rounded bg-forest px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-forest/90 disabled:cursor-not-allowed disabled:opacity-60 sm:self-end"
                    >
                        {exporting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Download className="h-4 w-4" />
                        )}
                        {exporting ? "Preparing CSV" : "Download CSV"}
                    </button>
                </div>
            </div>
            {error && (
                <div className="mt-3 rounded border border-coral/40 bg-coral-light px-3 py-2 text-sm font-semibold text-coral">
                    {error}
                </div>
            )}
        </div>
    );
};

const SeasonCoverageTable = ({
    seasons,
    linkedSeasonByYear = {},
    canRefresh = false,
    refreshing = false,
    canImportOldSeasons = false,
    importingOldSeasons = false,
    onImportOldSeasons,
    onRunRefresh,
    onRunSeasonBackfills,
    hasRefreshableIncomplete = false,
}) => (
    <div className="max-w-full min-w-0">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3 border-b-2 border-ds-text pb-2">
            <h2 className="font-display text-2xl font-bold text-ds-text">
                Season Coverage
            </h2>
            <div className="flex flex-wrap items-center gap-2">
                {canImportOldSeasons && (
                    <button
                        type="button"
                        disabled={importingOldSeasons}
                        onClick={onImportOldSeasons}
                        className="inline-flex items-center gap-2 rounded bg-forest px-3 py-2 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-forest/90 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {importingOldSeasons ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <History className="h-3.5 w-3.5" />
                        )}
                        {importingOldSeasons
                            ? "Importing Old Seasons"
                            : "Import Old Seasons"}
                    </button>
                )}
                {canRefresh && hasRefreshableIncomplete && (
                    <button
                        type="button"
                        disabled={refreshing}
                        onClick={onRunSeasonBackfills}
                        className="inline-flex items-center gap-2 rounded bg-forest px-3 py-2 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-forest/90 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        <RefreshCw
                            className={`h-3.5 w-3.5 ${
                                refreshing ? "animate-spin" : ""
                            }`}
                        />
                        Fetch Incomplete Seasons
                    </button>
                )}
            </div>
        </div>
        <div className="max-w-full overflow-x-auto overscroll-x-contain rounded border border-border bg-white">
            <table className="w-full min-w-[820px] text-left text-sm">
                <thead className="bg-surface text-xs font-bold uppercase tracking-wide text-text-muted">
                    <tr>
                        <th className="px-3 py-2">Year</th>
                        <th className="px-3 py-2">Link</th>
                        <th className="px-3 py-2 text-right">Rows</th>
                        <th className="px-3 py-2">Weeks</th>
                        <th className="px-3 py-2">Refresh</th>
                        <th className="px-3 py-2">Fetched</th>
                        <th className="px-3 py-2 text-right">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {seasons.map((season) => {
                        const status = seasonStatus(season);
                        const weeks = compactWeekList(season.completed_weeks);
                        return (
                            <tr
                                key={season.year}
                                className="border-t border-border text-text"
                            >
                                <td className="px-3 py-2 font-bold">
                                    {season.year}
                                </td>
                                <td className="px-3 py-2">
                                    <StatusPill
                                        tone={season.is_linked ? "good" : "warn"}
                                    >
                                        {season.is_linked ? "Linked" : "Unlinked"}
                                    </StatusPill>
                                </td>
                                <td className="px-3 py-2 text-right font-semibold">
                                    {formatInteger(season.row_count)}
                                </td>
                                <td className="px-3 py-2" title={weeks}>
                                    {weeks}
                                </td>
                                <td className="px-3 py-2">
                                    <StatusPill tone={status.tone}>
                                        {status.label}
                                    </StatusPill>
                                </td>
                                <td className="px-3 py-2 text-text-muted">
                                    {formatDate(
                                        season.data_refresh?.last_fetched_at ||
                                            season.latest_row_fetched_at
                                    )}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    {canRefresh &&
                                    season.can_refresh &&
                                    !season.data_refresh?.is_season_complete &&
                                    linkedSeasonByYear[String(season.year)]
                                        ?.league_id ? (
                                        <button
                                            type="button"
                                            disabled={refreshing}
                                            onClick={() =>
                                                onRunRefresh("season", {
                                                    year: String(season.year),
                                                    week: "",
                                                    provider:
                                                        linkedSeasonByYear[
                                                            String(season.year)
                                                        ]?.provider,
                                                    leagueId:
                                                        linkedSeasonByYear[
                                                            String(season.year)
                                                        ]?.league_id,
                                                })
                                            }
                                            className="inline-flex items-center gap-1.5 rounded border border-border px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-forest hover:bg-surface disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            <RefreshCw
                                                className={`h-3.5 w-3.5 ${
                                                    refreshing
                                                        ? "animate-spin"
                                                        : ""
                                                }`}
                                            />
                                            Fetch
                                        </button>
                                    ) : (
                                        <span className="text-xs font-semibold text-text-muted">
                                            -
                                        </span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    </div>
);

const MembersPanel = ({ members }) => (
    <div>
        <div className="mb-3 flex items-center gap-2 border-b-2 border-ds-text pb-2">
            <Users className="h-5 w-5 text-forest" />
            <h2 className="font-display text-2xl font-bold text-ds-text">
                Members
            </h2>
        </div>
        <div className="rounded border border-border bg-white">
            {members.length === 0 ? (
                <div className="px-4 py-6 text-sm font-semibold text-text-muted">
                    No linked members found.
                </div>
            ) : (
                members.map((member) => (
                    <div
                        key={member.user_id}
                        className="flex items-center justify-between border-t border-border px-4 py-3 first:border-t-0"
                    >
                        <div>
                            <p className="font-bold text-text">{member.username}</p>
                            {member.email && (
                                <p className="text-xs font-semibold text-text-muted">
                                    {member.email}
                                </p>
                            )}
                        </div>
                        {member.is_requesting_user && (
                            <StatusPill tone="good">You</StatusPill>
                        )}
                    </div>
                ))
            )}
        </div>
    </div>
);

const OwnerAliasesPanel = ({
    ownerRows,
    teamOwnerRows,
    ownerAliasMeta,
    editingOwnerName,
    editingCanonical,
    setEditingCanonical,
    savingOwnerName,
    onBeginAliasEdit,
    onCancelAliasEdit,
    onSaveAlias,
    onDeleteAlias,
}) => (
    <section>
        <div className="mb-3 flex flex-col gap-2 border-b-2 border-ds-text pb-2 sm:flex-row sm:items-end sm:justify-between">
            <h2 className="font-display text-2xl font-bold text-ds-text">
                Owner Name Aliases
            </h2>
            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
                {ownerAliasMeta?.seasonYears?.length > 0 && (
                    <span className="rounded-full border border-border bg-bg px-2 py-1 text-text-muted">
                        Seasons {ownerAliasMeta.seasonYears.join(", ")}
                    </span>
                )}
                {ownerAliasMeta?.conflictCount > 0 && (
                    <span className="rounded-full border border-red-200 bg-red-50 px-2 py-1 text-red-700">
                        {ownerAliasMeta.conflictCount} conflict
                        {ownerAliasMeta.conflictCount === 1 ? "" : "s"}
                    </span>
                )}
            </div>
        </div>
        <p className="mb-6 text-sm font-semibold text-text-muted">
            Keep individual owner aliases separate from the display names used
            when multiple owners share a team.
        </p>

        <h3 className="font-display text-xl font-bold text-ds-text">
            Co-owner team aliases
        </h3>
        <p className="mb-3 mt-1 text-sm font-semibold text-text-muted">
            These aliases apply only to the combined team label. Individual
            records continue to use each owner's alias below.
        </p>
        <div className="mb-8 overflow-hidden rounded border border-border bg-white">
            {teamOwnerRows === null ? (
                <div className="flex items-center gap-2 px-4 py-6 text-sm font-semibold text-text-muted">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading co-owner teams
                </div>
            ) : teamOwnerRows.length > 0 ? (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[860px] text-sm">
                        <thead className="bg-bg">
                            <tr className="border-b border-border">
                                <th className="px-4 py-3 text-left font-semibold text-text-muted">
                                    Provider team label
                                </th>
                                <th className="px-4 py-3 text-left font-semibold text-text-muted">
                                    Individual owners
                                </th>
                                <th className="px-4 py-3 text-left font-semibold text-text-muted">
                                    Seasons
                                </th>
                                <th className="px-4 py-3 text-left font-semibold text-text-muted">
                                    Team names
                                </th>
                                <th className="px-4 py-3 text-left font-semibold text-text-muted">
                                    Team alias
                                </th>
                                <th className="w-24 px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {teamOwnerRows.map((row) => {
                                const rowIdentity = `team:${row.pair_key}`;
                                const isEditing =
                                    editingOwnerName === rowIdentity;
                                const isSaving = savingOwnerName === rowIdentity;
                                return (
                                    <tr
                                        key={rowIdentity}
                                        className="align-top border-b border-border/60 last:border-0"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="break-words font-mono text-ds-text">
                                                {row.raw_name}
                                            </div>
                                            <div className="mt-1 text-[11px] uppercase tracking-wide text-text-muted">
                                                {row.has_alias
                                                    ? "Team alias"
                                                    : "Provider label"}
                                                {" · Name matched"}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-ds-text">
                                            {(row.member_names || []).join(" & ")}
                                        </td>
                                        <td className="px-4 py-3 text-ds-text">
                                            {(row.seasons || []).join(", ") || "-"}
                                        </td>
                                        <td className="px-4 py-3 text-ds-text">
                                            {(row.team_names || []).join(", ") || "-"}
                                        </td>
                                        <td className="px-4 py-3">
                                            {isEditing ? (
                                                <input
                                                    autoFocus
                                                    value={editingCanonical}
                                                    onChange={(event) =>
                                                        setEditingCanonical(
                                                            event.target.value
                                                        )
                                                    }
                                                    onKeyDown={(event) => {
                                                        if (event.key === "Enter") {
                                                            onSaveAlias(row);
                                                        }
                                                        if (event.key === "Escape") {
                                                            onCancelAliasEdit();
                                                        }
                                                    }}
                                                    aria-label={`Team alias for ${row.raw_name}`}
                                                    className="min-w-48 w-full rounded border border-mustard bg-bg px-2 py-1 text-sm text-ds-text focus:outline-none focus:ring-1 focus:ring-mustard"
                                                />
                                            ) : (
                                                <span className="break-words font-semibold text-ds-text">
                                                    {row.canonical_name}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-1">
                                                {isEditing ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                onSaveAlias(row)
                                                            }
                                                            disabled={
                                                                isSaving ||
                                                                !editingCanonical.trim() ||
                                                                editingCanonical.trim() ===
                                                                    row.canonical_name
                                                            }
                                                            className="p-1 text-green-600 hover:text-green-700 disabled:opacity-40"
                                                            title="Save team alias"
                                                        >
                                                            <Check className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={onCancelAliasEdit}
                                                            disabled={isSaving}
                                                            className="p-1 text-text-muted hover:text-ds-text disabled:opacity-40"
                                                            title="Cancel"
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                onBeginAliasEdit(row)
                                                            }
                                                            disabled={isSaving}
                                                            className="p-1 text-text-muted transition-colors hover:text-ds-text disabled:opacity-40"
                                                            title="Edit team alias"
                                                        >
                                                            <Pencil className="h-3.5 w-3.5" />
                                                        </button>
                                                        {row.has_alias && (
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    onDeleteAlias(row)
                                                                }
                                                                disabled={isSaving}
                                                                className="p-1 text-text-muted transition-colors hover:text-red-500 disabled:opacity-40"
                                                                title="Delete team alias"
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="p-4 text-sm italic text-text-muted">
                    No co-owner teams found in historical data yet.
                </p>
            )}
        </div>

        <h3 className="mb-3 font-display text-xl font-bold text-ds-text">
            Individual owner aliases
        </h3>

        <div className="overflow-hidden rounded border border-border bg-white">
            {ownerRows === null ? (
                <div className="flex items-center gap-2 px-4 py-6 text-sm font-semibold text-text-muted">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading owner aliases
                </div>
            ) : ownerRows.length > 0 ? (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[820px] text-sm">
                        <thead className="bg-bg">
                            <tr className="border-b border-border">
                                <th className="px-4 py-3 text-left font-semibold text-text-muted">
                                    Raw owner name
                                </th>
                                <th className="px-4 py-3 text-left font-semibold text-text-muted">
                                    Seasons
                                </th>
                                <th className="px-4 py-3 text-left font-semibold text-text-muted">
                                    Team names
                                </th>
                                <th className="px-4 py-3 text-left font-semibold text-text-muted">
                                    Canonical owner
                                </th>
                                <th className="px-4 py-3 text-left font-semibold text-text-muted">
                                    Conflicts
                                </th>
                                <th className="w-24 px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {ownerRows.map((row) => {
                                const rowIdentity =
                                    row.owner_key || `legacy:${row.raw_name}`;
                                const isEditing =
                                    editingOwnerName === rowIdentity;
                                const isSaving =
                                    savingOwnerName === rowIdentity;
                                return (
                                    <tr
                                        key={rowIdentity}
                                        className="align-top border-b border-border/60 last:border-0"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="break-words font-mono text-ds-text">
                                                {row.raw_name}
                                            </div>
                                            <div className="mt-1 text-[11px] uppercase tracking-wide text-text-muted">
                                                {row.has_alias
                                                    ? "Alias"
                                                    : row.inherits_alias
                                                      ? "Inherited alias"
                                                      : "Direct"}
                                                {row.owner_key ? " · ID matched" : " · Name matched"}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-ds-text">
                                            {row.seasons?.length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {row.seasons.map((season) => (
                                                        <span
                                                            key={season}
                                                            className="rounded-full bg-bg px-2 py-0.5 text-xs text-text-muted"
                                                        >
                                                            {season}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="italic text-text-muted">
                                                    No stored rows
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-ds-text">
                                            {row.team_names?.length > 0 ? (
                                                <div className="space-y-1">
                                                    {row.team_names
                                                        .slice(0, 3)
                                                        .map((teamName) => (
                                                            <div
                                                                key={teamName}
                                                                className="break-words"
                                                            >
                                                                {teamName}
                                                            </div>
                                                        ))}
                                                    {row.team_names.length > 3 && (
                                                        <div className="text-xs text-text-muted">
                                                            +{row.team_names.length - 3} more
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="italic text-text-muted">
                                                    None
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {isEditing ? (
                                                <input
                                                    autoFocus
                                                    value={editingCanonical}
                                                    onChange={(event) =>
                                                        setEditingCanonical(
                                                            event.target.value
                                                        )
                                                    }
                                                    onKeyDown={(event) => {
                                                        if (event.key === "Enter") {
                                                            onSaveAlias(row);
                                                        }
                                                        if (event.key === "Escape") {
                                                            onCancelAliasEdit();
                                                        }
                                                    }}
                                                    className="min-w-48 w-full rounded border border-mustard bg-bg px-2 py-1 text-sm text-ds-text focus:outline-none focus:ring-1 focus:ring-mustard"
                                                />
                                            ) : (
                                                <span className="break-words font-semibold text-ds-text">
                                                    {row.canonical_name}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {row.conflicts?.length > 0 ? (
                                                <div className="flex flex-col gap-1.5">
                                                    {row.conflicts.map((conflict) => (
                                                        <span
                                                            key={`${rowIdentity}-${conflict.code}-${conflict.message}`}
                                                            className={`rounded border px-2 py-1 text-xs ${
                                                                conflict.severity ===
                                                                "error"
                                                                    ? "border-red-200 bg-red-50 text-red-700"
                                                                    : "border-amber-200 bg-amber-50 text-amber-700"
                                                            }`}
                                                            title={conflict.message}
                                                        >
                                                            {conflict.message}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="italic text-text-muted">
                                                    None
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-1">
                                                {isEditing ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() => onSaveAlias(row)}
                                                            disabled={
                                                                isSaving ||
                                                                !editingCanonical.trim() ||
                                                                editingCanonical.trim() ===
                                                                    row.canonical_name
                                                            }
                                                            className="p-1 text-green-600 hover:text-green-700 disabled:opacity-40"
                                                            title="Save"
                                                        >
                                                            <Check className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={onCancelAliasEdit}
                                                            disabled={isSaving}
                                                            className="p-1 text-text-muted hover:text-ds-text disabled:opacity-40"
                                                            title="Cancel"
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                onBeginAliasEdit(row)
                                                            }
                                                            disabled={isSaving}
                                                            className="p-1 text-text-muted transition-colors hover:text-ds-text disabled:opacity-40"
                                                            title="Edit"
                                                        >
                                                            <Pencil className="h-3.5 w-3.5" />
                                                        </button>
                                                        {row.has_alias && (
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    onDeleteAlias(
                                                                        row
                                                                    )
                                                                }
                                                                disabled={isSaving}
                                                                className="p-1 text-text-muted transition-colors hover:text-red-500 disabled:opacity-40"
                                                                title="Delete"
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="p-6 text-sm italic text-text-muted">
                    No owner names found in historical data yet.
                </p>
            )}
        </div>
    </section>
);

const RecordsTab = ({
    records,
    loading,
    error,
    includeAllOwners,
    setIncludeAllOwners,
}) => {
    if (error) return <ErrorState message={error} />;
    if (loading && !records) return <RecordsSkeleton />;
    if (!records) return <EmptyState>No all-time records loaded yet.</EmptyState>;

    const hasCurrentScopeProjection = Array.isArray(
        records.current_owner_record_order
    );
    const hasAllScopeProjection = Array.isArray(records.all_owner_record_order);
    const currentScopeItems = (records.current_owner_record_order || [])
        .map((key) => records.current_owner_records?.[key])
        .filter(Boolean);
    const allScopeItems = (records.all_owner_record_order || records.record_order || [])
        .map((key) => (records.all_owner_records || records.records)?.[key])
        .filter(Boolean);
    const fallbackItems = (records.record_order || [])
        .map((key) => records.records?.[key])
        .filter(Boolean)
        .map((record) => ({
            ...record,
            holder: record.active_holder || record.holder,
        }))
        .filter((record) => record.holder);
    const recordItems = includeAllOwners
        ? hasAllScopeProjection
            ? allScopeItems
            : fallbackItems
        : hasCurrentScopeProjection
          ? currentScopeItems
          : fallbackItems;
    const bestRecords = recordItems.filter((record) => !isWorstRecord(record));
    const worstRecords = recordItems.filter(isWorstRecord);

    return (
        <section className="mt-6 space-y-4">
            <div className="flex flex-col gap-3 border-b-2 border-ds-text pb-3 lg:flex-row lg:items-end lg:justify-between">
                <div className="flex flex-wrap items-baseline gap-3">
                    <h2 className="font-display text-2xl font-bold text-ds-text">
                        All-Time Records
                    </h2>
                    <span className="text-sm font-semibold text-text-muted">
                        {records.years?.join(", ") || "No seasons"}
                    </span>
                </div>
                <OwnerScopeToggle
                    includeAllOwners={includeAllOwners}
                    setIncludeAllOwners={setIncludeAllOwners}
                    latestSeasonYear={records.latest_season_year}
                    currentOwnerCount={records.current_owner_count}
                    loading={loading}
                />
            </div>
            {recordItems.length === 0 ? (
                <EmptyState>No all-time records are derivable yet.</EmptyState>
            ) : (
                <div className="grid gap-6 lg:grid-cols-2">
                    <RecordColumn title="Best Records" records={bestRecords} />
                    <RecordColumn title="Worst Records" records={worstRecords} />
                </div>
            )}
        </section>
    );
};

const RecordColumn = ({ title, records }) => (
    <div className="space-y-3">
        <div className="border-b border-border pb-2">
            <h3 className="font-display text-xl font-bold text-ds-text">
                {title}
            </h3>
        </div>
        {records.length === 0 ? (
            <EmptyState>No records in this group yet.</EmptyState>
        ) : (
            records.map((record) => (
                <RecordCard key={record.key} record={record} />
            ))
        )}
    </div>
);

const RecordCard = ({ record }) => {
    const holder = record.holder || {};
    const hasBestTrioTooltip = ["best_trio", "lowest_best_trio"].includes(
        record.key
    );
    const groupLabel =
        recordGroupLabels[record.key] ||
        (record.key.includes("_pts") ? "Position Records" : "Records");

    return (
        <article className="rounded border border-border bg-white px-4 py-3">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-text-muted">
                        {groupLabel}
                    </p>
                    <h3 className="mt-1 font-display text-xl font-bold text-ds-text">
                        {hasBestTrioTooltip ? (
                            <span className="inline-flex items-center gap-2">
                                <span>{record.label}</span>
                                <Tooltip label={BEST_TRIO_TOOLTIP}>
                                    <button
                                        type="button"
                                        aria-label="About Best Trio"
                                        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-text-muted transition-colors hover:text-forest focus:outline-none focus:ring-2 focus:ring-mustard/70"
                                    >
                                        <Info size={17} strokeWidth={2.5} aria-hidden="true" />
                                    </button>
                                </Tooltip>
                            </span>
                        ) : (
                            record.label
                        )}
                    </h3>
                </div>
                <p className="whitespace-nowrap text-right text-2xl font-black text-forest">
                    {formatRecordValue(record)}
                </p>
            </div>
            <div className="mt-3 border-t border-border pt-3 text-sm">
                <p className="font-bold text-text">
                    {holder.team_owner || "-"}
                    {holder.team_name ? (
                        <span className="font-semibold text-text-muted">
                            {" "}
                            - {holder.team_name}
                        </span>
                    ) : null}
                </p>
                <p className="mt-1 text-xs font-semibold text-text-muted">
                    {holder.year || "-"}
                    {holder.week ? `, Week ${holder.week}` : ""}
                    {holder.opponent_owner
                        ? ` vs. ${holder.opponent_owner}`
                        : ""}
                </p>
            </div>
        </article>
    );
};

const LeaderboardsTab = ({
    leaderboards,
    loading,
    selectedLeaderboard,
    setSelectedLeaderboard,
    error,
    includeAllOwners,
    setIncludeAllOwners,
}) => {
    if (error) return <ErrorState message={error} />;
    if (loading && !leaderboards) {
        return <LeaderboardsSkeleton />;
    }
    if (!leaderboards) {
        return <EmptyState>No owner leaderboards loaded yet.</EmptyState>;
    }

    const selected =
        LEADERBOARD_OPTIONS.find((item) => item.key === selectedLeaderboard) ||
        LEADERBOARD_OPTIONS[0];
    const allRows = leaderboards.leaderboards?.[selected.key] || [];
    const rows = includeAllOwners
        ? allRows
        : rankLeaderboardRows(
              allRows.filter((row) => row.is_active_owner !== false)
          );
    const ownerCount = includeAllOwners
        ? leaderboards.owners?.length || 0
        : (leaderboards.current_owner_count ?? rows.length);

    return (
        <section className="mt-6 space-y-4">
            <div className="flex flex-col gap-3 border-b-2 border-ds-text pb-3 lg:flex-row lg:items-end lg:justify-between">
                <div>
                    <h2 className="font-display text-2xl font-bold text-ds-text">
                        Owner Leaderboards
                    </h2>
                    <p className="text-sm font-semibold text-text-muted">
                        {ownerCount} owners across{" "}
                        {leaderboards.years?.join(", ") || "no seasons"}
                    </p>
                </div>
                <div className="flex w-full flex-col gap-2 lg:w-auto lg:items-end">
                    <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                        <OwnerScopeToggle
                            includeAllOwners={includeAllOwners}
                            setIncludeAllOwners={setIncludeAllOwners}
                            latestSeasonYear={leaderboards.latest_season_year}
                            currentOwnerCount={leaderboards.current_owner_count}
                            loading={loading}
                        />
                        <select
                            value={selectedLeaderboard}
                            onChange={(event) =>
                                setSelectedLeaderboard(event.target.value)
                            }
                            className="w-full rounded border border-border bg-white px-3 py-2 text-sm font-semibold text-text sm:w-56"
                        >
                            {LEADERBOARD_OPTIONS.map((option) => (
                                <option key={option.key} value={option.key}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="overflow-x-auto rounded border border-border bg-white">
                <table className="w-full min-w-[680px] text-left text-sm">
                    <thead className="bg-surface text-xs font-bold uppercase tracking-wide text-text-muted">
                        <tr>
                            <th className="px-3 py-2">Rank</th>
                            <th className="px-3 py-2">Owner</th>
                            <th className="px-3 py-2">Team</th>
                            <th className="px-3 py-2 text-right">
                                {selected.label}
                            </th>
                            <th className="px-3 py-2 text-right">Games</th>
                            <th className="px-3 py-2">Seasons</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr
                                key={`${selected.key}-${row.rank}-${row.owner}`}
                                className="border-t border-border text-text"
                            >
                                <td className="px-3 py-2 font-black text-forest">
                                    {row.rank}
                                </td>
                                <td className="px-3 py-2 font-bold">{row.owner}</td>
                                <td className="px-3 py-2 text-text-muted">
                                    {row.team_name || "-"}
                                </td>
                                <td className="px-3 py-2 text-right font-black">
                                    {formatNumber(row.value, selected.format)}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    {row.games_played || 0}
                                </td>
                                <td className="px-3 py-2 text-text-muted">
                                    {(row.seasons || []).join(", ")}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

const RivalriesTab = ({
    rivalries,
    loading,
    creating,
    updatingId,
    deletingId,
    onCreate,
    onRename,
    onDelete,
    error,
}) => {
    const [name, setName] = useState("");
    const [selectedOwners, setSelectedOwners] = useState([]);

    const ownerOptions = useMemo(
        () => rivalries?.owner_options || [],
        [rivalries]
    );
    const rivalryItems = useMemo(() => rivalries?.rivalries || [], [rivalries]);

    useEffect(() => {
        const validOwners = new Set(
            ownerOptions.map((owner) => owner.owner_key || owner.owner_name)
        );
        setSelectedOwners((current) =>
            current.filter((ownerKey) => validOwners.has(ownerKey))
        );
    }, [ownerOptions]);

    if (error) return <ErrorState message={error} />;
    if (loading && !rivalries) return <RivalriesSkeleton />;
    if (!rivalries) return <EmptyState>No rivalry data loaded yet.</EmptyState>;

    const toggleOwner = (ownerName) => {
        setSelectedOwners((current) =>
            current.includes(ownerName)
                ? current.filter((item) => item !== ownerName)
                : [...current, ownerName]
        );
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const created = await onCreate({
            name: name.trim(),
            ownerMembers: selectedOwners.map((ownerKey) => {
                const option = ownerOptions.find(
                    (owner) => (owner.owner_key || owner.owner_name) === ownerKey
                );
                return option?.owner_key
                    ? {
                          owner_key: option.owner_key,
                          owner_name: option.owner_name,
                      }
                    : option?.owner_name || ownerKey;
            }),
        });
        if (created) {
            setName("");
            setSelectedOwners([]);
        }
    };

    return (
        <section className="mt-6 max-w-full min-w-0 space-y-6 overflow-hidden">
            <div className="flex flex-wrap items-baseline justify-between gap-3 border-b-2 border-ds-text pb-2">
                <div>
                    <h2 className="font-display text-2xl font-bold text-ds-text">
                        Rivalries
                    </h2>
                    <p className="text-sm font-semibold text-text-muted">
                        {rivalries.rivalry_count || 0} saved rivalries from{" "}
                        {ownerOptions.length} historical owners
                    </p>
                </div>
            </div>

            <div className="grid max-w-full min-w-0 gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.6fr)]">
                <form
                    onSubmit={handleSubmit}
                    className="min-w-0 rounded border border-border bg-white px-3 py-4 sm:px-4"
                >
                    <div className="space-y-4">
                        <label className="block text-sm font-bold text-text">
                            Rivalry Name
                            <input
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                                placeholder="Optional"
                                className="mt-1 w-full rounded border border-border px-3 py-2 text-sm font-semibold"
                            />
                        </label>

                        <div>
                            <p className="text-sm font-bold text-text">Owners</p>
                            {ownerOptions.length === 0 ? (
                                <EmptyState>
                                    No historical owners are available yet.
                                </EmptyState>
                            ) : (
                                <div className="mt-2 max-h-[26rem] max-w-full overflow-y-auto rounded border border-border">
                                    {ownerOptions.map((owner) => (
                                        <label
                                            key={owner.owner_key || owner.owner_name}
                                            className="flex cursor-pointer items-start gap-3 border-t border-border px-3 py-2 first:border-t-0 hover:bg-surface"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedOwners.includes(
                                                    owner.owner_key || owner.owner_name
                                                )}
                                                onChange={() =>
                                                    toggleOwner(
                                                        owner.owner_key || owner.owner_name
                                                    )
                                                }
                                                className="mt-1 h-4 w-4 rounded border-border text-forest"
                                            />
                                            <span className="min-w-0">
                                                <span className="block break-words font-bold text-text">
                                                    {owner.owner_name}
                                                </span>
                                                <span className="block break-words text-xs font-semibold text-text-muted">
                                                    {(owner.seasons || []).join(", ") ||
                                                        "No seasons"}
                                                </span>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={creating || selectedOwners.length < 2}
                            className="inline-flex w-full items-center justify-center gap-2 rounded bg-forest px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-forest/90 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {creating ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Plus className="h-4 w-4" />
                            )}
                            {creating ? "Saving Rivalry" : "Save Rivalry"}
                        </button>
                    </div>
                </form>

                <div className="min-w-0 space-y-4">
                    {rivalryItems.length === 0 ? (
                        <EmptyState>No rivalries have been defined yet.</EmptyState>
                    ) : (
                        rivalryItems.map((rivalry) => (
                            <RivalryCard
                                key={rivalry.id}
                                rivalry={rivalry}
                                updating={updatingId === rivalry.id}
                                deleting={deletingId === rivalry.id}
                                onRename={onRename}
                                onDelete={onDelete}
                            />
                        ))
                    )}
                </div>
            </div>
        </section>
    );
};

const RivalryCard = ({ rivalry, updating, deleting, onRename, onDelete }) => {
    const [isEditingName, setIsEditingName] = useState(false);
    const [draftName, setDraftName] = useState(
        rivalry.custom_name || rivalry.name || ""
    );
    const summary = rivalry.summary || {};
    const ownerRecords =
        summary.owner_records || rivalry.head_to_head?.owners || [];
    const pairs = rivalry.head_to_head?.pairs || [];
    const recentMatchups = rivalry.recent_matchups || [];
    const sortedOwnerRecords = [...ownerRecords].sort((a, b) => {
        const winPctDiff =
            Number(b.win_percentage || 0) - Number(a.win_percentage || 0);
        if (winPctDiff !== 0) return winPctDiff;
        const winsDiff = Number(b.wins || 0) - Number(a.wins || 0);
        if (winsDiff !== 0) return winsDiff;
        const pointsDiff = Number(b.points_for || 0) - Number(a.points_for || 0);
        if (pointsDiff !== 0) return pointsDiff;
        return String(a.owner || "").localeCompare(String(b.owner || ""));
    });
    const highestScoreRecord = rivalry.records?.highest_single_game_score;
    const showPairRecords = Number(rivalry.member_count || 0) >= 3;

    useEffect(() => {
        if (!isEditingName) {
            setDraftName(rivalry.custom_name || rivalry.name || "");
        }
    }, [isEditingName, rivalry.custom_name, rivalry.name]);

    const submitName = async (event) => {
        event.preventDefault();
        const saved = await onRename(rivalry.id, draftName.trim());
        if (saved) {
            setIsEditingName(false);
        }
    };

    const cancelNameEdit = () => {
        setDraftName(rivalry.custom_name || rivalry.name || "");
        setIsEditingName(false);
    };

    return (
        <article className="min-w-0 max-w-full overflow-hidden rounded border border-border bg-white px-3 py-4 sm:px-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold uppercase tracking-wide text-text-muted">
                        {rivalry.member_count} owners
                    </p>
                    {isEditingName ? (
                        <form
                            onSubmit={submitName}
                            className="mt-1 flex flex-col gap-2 sm:flex-row"
                        >
                            <label className="sr-only" htmlFor={`rivalry-name-${rivalry.id}`}>
                                Edit rivalry name for {rivalry.name}
                            </label>
                            <input
                                id={`rivalry-name-${rivalry.id}`}
                                value={draftName}
                                onChange={(event) =>
                                    setDraftName(event.target.value)
                                }
                                placeholder={rivalry.name}
                                className="min-w-0 flex-1 rounded border border-border px-3 py-2 text-sm font-semibold text-text"
                                autoFocus
                            />
                            <div className="flex gap-2">
                                <button
                                    type="submit"
                                    disabled={updating}
                                    className="inline-flex items-center justify-center gap-1.5 rounded bg-forest px-3 py-2 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-forest/90 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    <Check className="h-3.5 w-3.5" />
                                    Save Name
                                </button>
                                <button
                                    type="button"
                                    disabled={updating}
                                    onClick={cancelNameEdit}
                                    className="inline-flex items-center justify-center gap-1.5 rounded border border-border px-3 py-2 text-xs font-bold uppercase tracking-wide text-forest transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    <X className="h-3.5 w-3.5" />
                                    Cancel
                                </button>
                            </div>
                        </form>
                    ) : (
                        <h3 className="break-words font-display text-2xl font-bold text-ds-text">
                            {rivalry.name}
                        </h3>
                    )}
                    <p className="mt-1 break-words text-sm font-semibold text-text-muted">
                        {(rivalry.members || [])
                            .map((member) => member.owner_name)
                            .join(" vs. ")}
                    </p>
                </div>
                {rivalry.can_manage && (
                    <div className="flex flex-wrap gap-2">
                        {!isEditingName && (
                            <button
                                type="button"
                                disabled={deleting || updating}
                                onClick={() => setIsEditingName(true)}
                                className="inline-flex items-center justify-center gap-2 rounded border border-border px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-forest transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <Pencil className="h-3.5 w-3.5" />
                                Edit Name
                            </button>
                        )}
                        <button
                            type="button"
                            disabled={deleting || updating}
                            onClick={() => onDelete(rivalry.id)}
                            className="inline-flex items-center justify-center gap-2 rounded border border-coral/40 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-coral transition-colors hover:bg-coral-light disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                        </button>
                    </div>
                )}
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <RivalryMetric
                    label="Matchups"
                    value={summary.total_matchups || 0}
                />
                <RivalryMetric
                    label="Playoff Meetings"
                    value={summary.playoff_meetings || 0}
                />
                <RivalryMetric
                    label="Highest Score"
                    value={formatNumber(highestScoreRecord?.value)}
                    detail={formatHighestScoreDetail(highestScoreRecord)}
                />
            </div>

            <div className="mt-4 max-w-full overflow-x-auto overscroll-x-contain rounded border border-border">
                <table className="w-full min-w-[620px] text-left text-sm">
                    <thead className="bg-surface text-xs font-bold uppercase tracking-wide text-text-muted">
                        <tr>
                            <th className="px-3 py-2">Owner</th>
                            <th className="px-3 py-2 text-right">Record</th>
                            <th className="px-3 py-2 text-right">Win %</th>
                            <th className="px-3 py-2 text-right">PF</th>
                            <th className="px-3 py-2 text-right">PA</th>
                            <th className="px-3 py-2 text-right">Playoffs</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedOwnerRecords.map((owner) => (
                            <tr
                                key={owner.owner_key || owner.owner}
                                className="border-t border-border text-text"
                            >
                                <td className="px-3 py-2 font-bold">
                                    {owner.owner}
                                </td>
                                <td className="px-3 py-2 text-right font-semibold">
                                    {owner.wins}-{owner.losses}-{owner.ties}
                                </td>
                                <td className="px-3 py-2 text-right font-semibold">
                                    {formatNumber(
                                        owner.win_percentage,
                                        "percent"
                                    )}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    {formatDecimal(owner.points_for)}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    {formatDecimal(owner.points_against)}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    {owner.playoff_wins}-{owner.playoff_losses}-
                                    {owner.playoff_ties}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showPairRecords && pairs.length > 0 && (
                <div className="mt-4">
                    <h4 className="text-sm font-bold uppercase tracking-wide text-text-muted">
                        Pair Records
                    </h4>
                    <div className="mt-2 grid min-w-0 gap-2 md:grid-cols-2">
                        {pairs.map((pair) => (
                            <div
                                key={(pair.owner_keys || pair.owners).join("-")}
                                className="min-w-0 rounded border border-border px-3 py-2 text-sm"
                            >
                                <p className="break-words font-bold text-text">
                                    {formatPairRecordTitle(pair)}
                                </p>
                                <p className="text-xs font-semibold text-text-muted">
                                    {pair.total_matchups || 0} matchups,{" "}
                                    {pair.playoff_meetings || 0} playoff
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {recentMatchups.length > 0 && (
                <div className="mt-4">
                    <h4 className="text-sm font-bold uppercase tracking-wide text-text-muted">
                        Recent Matchups
                    </h4>
                    <div className="mt-2 space-y-2">
                        {recentMatchups.slice(0, 5).map((matchup) => (
                            <div
                                key={`${matchup.year}-${matchup.week}-${matchup.teams
                                    .map((team) => team.owner)
                                    .join("-")}`}
                                className="flex min-w-0 flex-wrap items-center justify-between gap-2 border-t border-border pt-2 text-sm first:border-t-0 first:pt-0"
                            >
                                <span className="break-words font-bold text-text">
                                    {matchup.year} Week {matchup.week}
                                    {matchup.is_playoff ? " Playoff" : ""}
                                </span>
                                <span className="min-w-0 break-words text-right font-semibold text-text-muted">
                                    {matchup.teams
                                        .map(
                                            (team) =>
                                                `${team.owner} ${formatNumber(
                                                    team.score
                                                )}`
                                        )
                                        .join(" - ")}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </article>
    );
};

const RivalryMetric = ({ label, value, detail }) => (
    <div className="border-l-4 border-mustard bg-surface px-3 py-2">
        <p className="text-xs font-bold uppercase tracking-wide text-text-muted">
            {label}
        </p>
        <p className="mt-1 text-xl font-black text-forest">{value}</p>
        {detail && (
            <p className="mt-1 text-xs font-semibold text-text-muted">{detail}</p>
        )}
    </div>
);

const SeasonBrowserTab = ({
    coverage,
    loading,
    canRefresh,
    onRefreshSeason,
    error,
}) => {
    if (error) return <ErrorState message={error} />;
    if (loading && !coverage) {
        return <CoverageDependentSkeleton title="Season Browser" cards={4} />;
    }
    if (!coverage) return <EmptyState>No seasons loaded yet.</EmptyState>;

    const seasonsByYear = new Map(
        (coverage.linked_seasons || []).map((season) => [season.year, season])
    );

    return (
        <section className="mt-6 space-y-4">
            <div className="border-b-2 border-ds-text pb-2">
                <h2 className="font-display text-2xl font-bold text-ds-text">
                    Season Browser
                </h2>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
                {(coverage.season_coverage || []).map((season) => {
                    const linkedSeason = seasonsByYear.get(season.year);
                    const status = seasonStatus(season);
                    const canRefreshSeason =
                        canRefresh &&
                        Boolean(season.can_refresh || linkedSeason?.can_refresh);
                    return (
                        <article
                            key={season.year}
                            className="rounded border border-border bg-white px-4 py-3"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-wide text-text-muted">
                                        Season
                                    </p>
                                    <h3 className="font-display text-2xl font-bold text-ds-text">
                                        {season.year}
                                    </h3>
                                </div>
                                <StatusPill tone={status.tone}>
                                    {status.label}
                                </StatusPill>
                            </div>
                            <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                                <div>
                                    <p className="text-xs font-bold uppercase text-text-muted">
                                        Rows
                                    </p>
                                    <p className="font-black text-forest">
                                        {season.row_count}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs font-bold uppercase text-text-muted">
                                        Weeks
                                    </p>
                                    <p className="font-black text-forest">
                                        {compactWeekList(season.completed_weeks)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs font-bold uppercase text-text-muted">
                                        Last Week
                                    </p>
                                    <p className="font-black text-forest">
                                        {season.last_week || "-"}
                                    </p>
                                </div>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {linkedSeason && (
                                    <Link
                                        to={`/fantasy_stats/league/${linkedSeason.provider}/${linkedSeason.year}/${linkedSeason.league_id}/`}
                                        className="rounded border border-border px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-forest hover:bg-surface"
                                    >
                                        Open Season
                                    </Link>
                                )}
                                {canRefreshSeason && linkedSeason && (
                                    <button
                                        type="button"
                                        onClick={() => onRefreshSeason(season.year)}
                                        className="inline-flex items-center gap-1.5 rounded border border-border px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-forest hover:bg-surface"
                                    >
                                        <RefreshCw className="h-3.5 w-3.5" />
                                        Refresh
                                    </button>
                                )}
                            </div>
                        </article>
                    );
                })}
            </div>
        </section>
    );
};

const RefreshTab = ({
    coverage,
    loading,
    canRefresh,
    refreshTarget,
    setRefreshTarget,
    linkedSeasonTargets,
    refreshableSeasonTargets,
    refreshing,
    refreshResult,
    refreshError,
    refreshActivity,
    onRunRefresh,
    error,
}) => {
    const [weekDrafts, setWeekDrafts] = useState({});
    if (error) return <ErrorState message={error} />;
    if (loading && !coverage) {
        return <CoverageDependentSkeleton title="Refresh Status" cards={3} />;
    }

    const incompleteSeasons = (coverage?.season_coverage || []).filter(
        (season) => !season.data_refresh?.is_season_complete
    );
    const refreshOptions = canRefresh ? refreshableSeasonTargets : linkedSeasonTargets;

    return (
        <section className="mt-6 min-w-0 space-y-6">
            <RefreshActivityPanel
                activity={refreshActivity}
                result={refreshResult}
                error={refreshError}
            />
            <div className="grid max-w-full min-w-0 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
                <div className="min-w-0">
                    <div className="mb-3 border-b-2 border-ds-text pb-2">
                        <h2 className="font-display text-2xl font-bold text-ds-text">
                            Manual Refresh
                        </h2>
                    </div>
                    <div className="min-w-0 rounded border border-border bg-white px-3 py-4 sm:px-4">
                        {!canRefresh ? (
                            <div className="space-y-3">
                                <StatusPill tone="warn">Owner/Admin Only</StatusPill>
                                <p className="text-sm font-semibold text-text-muted">
                                    Refresh actions are hidden for saved leagues and
                                    non-owner historical group members.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {refreshOptions.length === 0 ? (
                                    <EmptyState>
                                        No linked seasons are refreshable by this
                                        account.
                                    </EmptyState>
                                ) : (
                                    <>
                                        <label className="block text-sm font-bold text-text">
                                            Season
                                            <select
                                                value={refreshTarget.year}
                                                onChange={(event) =>
                                                    setRefreshTarget((prev) => ({
                                                        ...prev,
                                                        year: event.target.value,
                                                    }))
                                                }
                                                className="mt-1 w-full min-w-0 rounded border border-border px-3 py-2 text-sm font-semibold"
                                            >
                                                {refreshOptions.map((season) => (
                                                    <option
                                                        key={`${season.provider}-${season.year}-${season.leagueId}`}
                                                        value={season.year}
                                                    >
                                                        {season.year} -{" "}
                                                        {season.leagueName ||
                                                            season.leagueId}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className="block text-sm font-bold text-text">
                                            Week
                                            <input
                                                value={refreshTarget.week}
                                                onChange={(event) =>
                                                    setRefreshTarget((prev) => ({
                                                        ...prev,
                                                        week: event.target.value,
                                                    }))
                                                }
                                                inputMode="numeric"
                                                placeholder="Optional"
                                                className="mt-1 w-full min-w-0 rounded border border-border px-3 py-2 text-sm font-semibold"
                                            />
                                        </label>
                                        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                                            <button
                                                type="button"
                                                disabled={refreshing}
                                                onClick={() => onRunRefresh("season")}
                                                className="inline-flex w-full items-center justify-center gap-2 rounded bg-forest px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-forest/90 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                                            >
                                                <RefreshCw
                                                    className={`h-4 w-4 ${
                                                        refreshing
                                                            ? "animate-spin"
                                                            : ""
                                                    }`}
                                                />
                                                Refresh Season
                                            </button>
                                            <button
                                                type="button"
                                                disabled={refreshing}
                                                onClick={() => onRunRefresh("week")}
                                                className="inline-flex w-full items-center justify-center gap-2 rounded border border-border px-4 py-2 text-sm font-bold text-forest transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                                            >
                                                <RefreshCw
                                                    className={`h-4 w-4 ${
                                                        refreshing
                                                            ? "animate-spin"
                                                            : ""
                                                    }`}
                                                />
                                                Refresh Week
                                            </button>
                                        </div>
                                    </>
                                )}
                                {refreshError && <ErrorState message={refreshError} />}
                                {refreshResult && (
                                    <div className="min-w-0 rounded border border-border bg-surface px-3 py-3 text-sm">
                                        <div className="flex min-w-0 items-center gap-2 font-bold text-forest">
                                            <CheckCircle2 className="h-4 w-4" />
                                            <span className="min-w-0 break-words">
                                                {refreshResult.status}
                                            </span>
                                        </div>
                                        <p className="mt-1 break-words text-text-muted">
                                            {refreshResult.rows_upserted || 0} rows
                                            upserted
                                            {refreshResult.week
                                                ? ` for week ${refreshResult.week}`
                                                : ""}{" "}
                                            in {refreshResult.year}.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <IncompleteSeasonControls
                        seasons={incompleteSeasons}
                        canRefresh={canRefresh}
                        refreshing={refreshing}
                        weekDrafts={weekDrafts}
                        setWeekDrafts={setWeekDrafts}
                        onRunRefresh={onRunRefresh}
                    />
                </div>

                <div className="min-w-0">
                    <div className="mb-3 border-b-2 border-ds-text pb-2">
                        <h2 className="font-display text-2xl font-bold text-ds-text">
                            Refresh Status
                        </h2>
                    </div>
                    <SeasonCoverageTable
                        seasons={coverage?.season_coverage || []}
                    />
                </div>
            </div>
        </section>
    );
};

const RefreshActivityPanel = ({ activity, result, error }) => {
    if (!activity && !result && !error) return null;

    const status = activity?.status || (error ? "failed" : "succeeded");
    const isRunning = status === "running";
    const isFailed = status === "failed";
    const Icon = isRunning ? Loader2 : isFailed ? AlertTriangle : CheckCircle2;
    const label = isRunning
        ? activity?.resultStatus === "refresh_in_progress"
            ? "Refresh already running"
            : "Refresh in progress"
        : isFailed
        ? "Refresh failed"
        : "Refresh complete";
    const target =
        activity?.mode === "week" && activity?.week
            ? `${activity.year} week ${activity.week}`
            : activity?.year
            ? `${activity.year} full season`
            : "Selected season";

    return (
        <div
            className={`min-w-0 rounded border px-3 py-3 text-sm sm:px-4 ${
                isFailed
                    ? "border-coral/40 bg-coral-light"
                    : isRunning
                    ? "border-mustard/40 bg-mustard/15"
                    : "border-green-200 bg-green-50"
            }`}
        >
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2 font-bold text-forest">
                    <Icon
                        className={`h-4 w-4 shrink-0 ${
                            isRunning ? "animate-spin" : ""
                        }`}
                    />
                    <span className="min-w-0 break-words">{label}</span>
                </div>
                <span className="min-w-0 break-words text-xs font-bold uppercase tracking-wide text-text-muted">
                    {target}
                </span>
            </div>
            <div className="mt-2 grid gap-2 text-xs font-semibold text-text-muted sm:grid-cols-3">
                <span className="min-w-0 break-words">
                    Started: {formatDate(activity?.startedAt)}
                </span>
                <span className="min-w-0 break-words">
                    Finished: {formatDate(activity?.finishedAt)}
                </span>
                <span className="min-w-0 break-words">
                    Rows:{" "}
                    {activity?.rowsUpserted ??
                        result?.rows_upserted ??
                        0}
                </span>
            </div>
            {(activity?.error || error) && (
                <p className="mt-2 break-words text-sm font-semibold text-coral">
                    {activity?.error || error}
                </p>
            )}
        </div>
    );
};

const IncompleteSeasonControls = ({
    seasons,
    canRefresh,
    refreshing,
    weekDrafts,
    setWeekDrafts,
    onRunRefresh,
}) => (
    <div className="mt-6 min-w-0">
        <div className="mb-3 flex items-center gap-2 border-b-2 border-ds-text pb-2">
            <RefreshCw className="h-5 w-5 text-forest" />
            <h2 className="font-display text-2xl font-bold text-ds-text">
                Backfill Needed
            </h2>
        </div>
        <div className="min-w-0 rounded border border-border bg-white">
            {seasons.length === 0 ? (
                <div className="px-4 py-6 text-sm font-semibold text-text-muted">
                    All linked seasons are marked complete.
                </div>
            ) : (
                seasons.map((season) => {
                    const status = seasonStatus(season);
                    const yearKey = String(season.year);
                    const weekDraft = weekDrafts[yearKey] || "";
                    const canRefreshSeason = canRefresh && season.can_refresh;
                    return (
                        <div
                            key={season.year}
                            className="border-t border-border px-3 py-3 first:border-t-0 sm:px-4"
                        >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <p className="font-display text-xl font-bold text-ds-text">
                                            {season.year}
                                        </p>
                                        <StatusPill tone={status.tone}>
                                            {status.label}
                                        </StatusPill>
                                    </div>
                                    <p className="mt-1 break-words text-xs font-semibold text-text-muted">
                                        {season.row_count} stored rows / weeks{" "}
                                        {compactWeekList(season.completed_weeks)} /
                                        last refresh{" "}
                                        {formatDate(
                                            season.data_refresh?.last_fetched_at ||
                                                season.latest_row_fetched_at
                                        )}
                                    </p>
                                </div>
                                {!canRefreshSeason && (
                                    <StatusPill tone="neutral">View Only</StatusPill>
                                )}
                            </div>
                            {canRefreshSeason && (
                                <div className="mt-3 grid min-w-0 gap-2 sm:flex sm:flex-wrap sm:items-end">
                                    <button
                                        type="button"
                                        disabled={refreshing}
                                        onClick={() =>
                                            onRunRefresh("season", {
                                                year: yearKey,
                                                week: "",
                                            })
                                        }
                                        className="inline-flex w-full items-center justify-center gap-2 rounded bg-forest px-3 py-2 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-forest/90 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                                    >
                                        <RefreshCw
                                            className={`h-3.5 w-3.5 ${
                                                refreshing ? "animate-spin" : ""
                                            }`}
                                        />
                                        Backfill Season
                                    </button>
                                    <label className="grid gap-1 text-xs font-bold uppercase tracking-wide text-text-muted sm:block">
                                        Week
                                        <input
                                            value={weekDraft}
                                            onChange={(event) =>
                                                setWeekDrafts((prev) => ({
                                                    ...prev,
                                                    [yearKey]: event.target.value,
                                                }))
                                            }
                                            inputMode="numeric"
                                            className="w-full min-w-0 rounded border border-border px-2 py-1.5 text-sm font-semibold text-text sm:ml-2 sm:w-20"
                                        />
                                    </label>
                                    <button
                                        type="button"
                                        disabled={refreshing || !weekDraft}
                                        onClick={() =>
                                            onRunRefresh("week", {
                                                year: yearKey,
                                                week: weekDraft,
                                            })
                                        }
                                        className="inline-flex w-full items-center justify-center gap-2 rounded border border-border px-3 py-2 text-xs font-bold uppercase tracking-wide text-forest transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                                    >
                                        <RefreshCw
                                            className={`h-3.5 w-3.5 ${
                                                refreshing ? "animate-spin" : ""
                                            }`}
                                        />
                                        Backfill Week
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })
            )}
        </div>
    </div>
);

export default LeagueHistoryPage;
